import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import {
	ReactFlow,
	ReactFlowProvider,
	Controls,
	Background,
	BackgroundVariant,
	MiniMap,
	useNodesState,
	useEdgesState,
	useReactFlow,
	useUpdateNodeInternals,
	type NodeTypes,
	type EdgeTypes,
	type NodeChange,
	type EdgeChange,
	type Node,
	type Connection,
	type Edge,
} from "@xyflow/react";
import { makeStyles, tokens, Spinner, Text, mergeClasses } from "@fluentui/react-components";
import { ottoTokens } from "@/theme/ottoTokens";
import { useWorkflowStore, findScopeContext, type InsertionPoint } from "@/stores/workflowStore";
import { useWorkflowLayoutStore } from "@/stores/workflowLayoutStore";
import {
	FOREACH_NESTED_MESSAGE,
	FOREACH_NESTED_TITLE,
} from "@/utils/validation";
import { useConnectionStore } from "@/stores/connectionStore";
import { useEditorRunStore } from "@/stores/editorRunStore";
import { useAgentChatAguiStore } from "@/stores/agentChatAguiStore";
import { useClipboardStore } from "@/stores/clipboardStore";
import { useNotificationStore } from "@/stores/notificationStore";
import { useShallow } from "zustand/react/shallow";
import { useWorkflowGraph, type GraphNode, type GraphEdge } from "@/hooks/useWorkflowGraph";
import { usePreloadOperations } from "@/hooks/useOperations";
import type { NodeMouseHandler } from "@xyflow/react";
import type { NodeExecution } from "@/types/monitoring";
import type { AgentIterationInfo } from "@/services/monitoringApi";
import {
	PhantomNode,
	PlaceholderTriggerNode,
	FloatingTriggerNode,
	FloatingActionNode,
	FloatingAgentNode,
	FloatingToolNode,
	FloatingModelNode,
	FloatingIfNode,
	FloatingSwitchNode,
	FloatingForeachNode,
	NoteNode,
	ScopeContainerNode,
	MergeNode,
	type FloatingAgentNodeData,
} from "./nodes";
import {
	AddButtonEdge,
	TerminalEdge,
	type TerminalEdgeData,
	DottedEdge,
} from "./edges";
import {
	computeTerminalPhantomPosition,
	findScopedBranchInsertion,
	getNodeOutputHandles,
	DEFAULT_TRIGGER_POSITION,
	createActionFromOperation,
	createTriggerFromOperation,
	buildConnectorInputs,
	extractOperationId,
	calculateInsertionPosition,
	calculateFloatingNodePosition,
	hasNodesWithoutPositions,
	getConditionSummary,
	countExpectedEdges,
	countParallelSiblings,
} from "./helpers";
import { CanvasContextMenu } from "./CanvasContextMenu";
import { CanvasControls } from "./CanvasControls";
import { NodeContextMenu } from "./NodeContextMenu";
import { NoteContextMenu } from "./NoteContextMenu";
import { EdgeContextMenu } from "./EdgeContextMenu";
import { TemplateGalleryDialog } from "./panels";
import { PickerPanelHost, type PickerView } from "./panels/PickerPanelHost";
import { ToolTypeMenu, type ToolType } from "./panels/ToolTypeMenu";
import type { AgentSelection } from "./panels/AgentTypeView";
import { buildAgentOperationFromSelection } from "./helpers/agentVariant";
import { EmptyCanvasState } from "./EmptyCanvasState";
import { MiniMapNode } from "./MiniMapNode";
import { CanvasSkeletonState } from "./CanvasSkeletonState";
import { NodeEditor } from "./NodeEditor";
import { useNodeEditorStore, type ScopeContext } from '@/stores/nodeEditorStore';
import {
	AGENT_ADD_TOOL_EVENT,
	type AgentAddToolEventDetail,
} from './NodeEditor/compact/AgentToolsSection';
import { shouldSkipShortcut, matchesShortcut, isPrimaryMod } from '@/utils/input';
import {
	getActionHostApiId,
	getActionReferenceName,
	getServiceProviderConnectorId,
	isManagedApiConnector,
	getValidConnections,
} from '@/utils/connections';
import { DockToolbar } from "./FloatingCommandBar";

import { CanvasSearch } from "./CanvasSearch";
import type { ValidationErrorClickInfo } from "./ValidationErrorsPanel";
import type { OperationDefinition } from "@/types/operations";
import { AGENT_OPERATIONS } from "@/types/operations";
import { useToolboxDragDrop } from "@/hooks/useToolboxDragDrop";
import { useCanvasKeyboardNavigation } from "@/hooks/useCanvasKeyboardNavigation";
import type {
	Action,
	AgentTool,
	WorkflowDefinition,
} from "@/types/workflowDefinition";
import { isForEachFull } from "@/hooks/useForEachConstraint";
import { useToggleLayoutOrientation, useIsCanvasLocked, useToggleCanvasLock } from "@/hooks/useLayoutOrientation";
import {
	calculateLayout,
	convertToLayoutInput,
	calculateScopeContainerBounds,
	orientationToDirection,
	DEFAULT_NODE_WIDTH,
	DEFAULT_NODE_HEIGHT,
} from "@/utils/workflow";
import type { Position } from "@/types/workflowDefinition";
import type { Note } from "@/types/note";
import type { MCPServerConfig } from "@/types/mcpTypes";
import type { CallableWorkflow } from "@/hooks/useCallableWorkflows";
import { useAppStore } from "@/stores/appStore";
import { useDraftModeStore, selectIsReadOnly } from "@/stores/draftModeStore";
import { useAuth } from "@/auth/useAuth";
import { useQueryClient } from "@tanstack/react-query";
import { fetchConnectorSwagger } from "@/services/operationsService";
import {
	CONNECTOR_SWAGGER_STALE_TIME,
	connectorSwaggerQueryKey,
	fetchConnectorSwaggerResult,
} from "@/hooks/useConnectorSwagger";
import { SwaggerParser } from "@/parsers/SwaggerParser";
import { useSearchParams } from "react-router-dom";
import { DraftModeProvider } from "@/contexts/DraftModeContext";
import { telemetryService } from "@/services/telemetryService";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { useTemplates } from "@/hooks/useTemplates";
import { useTemplateApply } from "@/hooks/useTemplateApply";

import "@xyflow/react/dist/style.css";
import { useConnections } from "@/hooks/useConnectionMutations";

// Duration the canvas container holds `data-otto-relayout="true"` after a
// Format Workflow / explicit re-layout. Matches `tokens.durationNormal`
// (200ms) plus a small safety margin so the CSS transition has time to
// run end-to-end before the attribute is removed.
const RELAYOUT_TRANSITION_MS = 350;

// xyflow caches each node's handle DOM bounds and won't refresh them on
// its own when handles move (orientation flip, terminal phantom mount/unmount,
// node delete that leaves surviving edges) — stale bounds = edges that render
// nowhere until the user pans/zooms. Defers the remeasure one frame so the
// new handle DOM is committed first. JSDOM lacks DOMMatrixReadOnly that
// xyflow needs to measure, so the effect is a no-op in tests.
const scheduleHandleRemeasure = (
	nodeIds: Iterable<string>,
	updateNodeInternals: (id: string) => void,
): (() => void) | undefined => {
	if (typeof window === "undefined" || typeof window.DOMMatrixReadOnly !== "function") return;
	const unique = Array.from(new Set(nodeIds));
	if (unique.length === 0) return;
	const raf = requestAnimationFrame(() => {
		for (const id of unique) updateNodeInternals(id);
	});
	return () => cancelAnimationFrame(raf);
};

const useStyles = makeStyles({
	wrapper: {
		display: "flex",
		width: "100%",
		height: "100%",
		flex: 1, // Fill remaining space when in flex container (e.g., with debug banner)
		minHeight: 0, // Allow shrinking in flex container
		minWidth: 0, // Allow shrinking in flex container (prevent horizontal overflow)
		position: "relative", // For absolute positioning of the panel
		overflow: "hidden", // Prevent any children from extending beyond bounds
	},
	container: {
		flex: 1,
		height: "100%",
		minWidth: 0, // Allow shrinking in flex container (prevent horizontal overflow)
		position: "relative", // Needed for FloatingCommandBar absolute positioning
		overflow: "hidden", // Prevent toolbar from rendering outside container
		backgroundColor: tokens.colorNeutralBackground1,
		"& .react-flow__controls": {
			backgroundColor: tokens.colorNeutralBackground1,
			border: `1px solid ${tokens.colorNeutralStroke1}`,
			borderRadius: tokens.borderRadiusMedium,
			// Offset from bottom to avoid overlap with dock toolbar
			bottom: "60px",
		},
		"& .react-flow__controls-button": {
			backgroundColor: tokens.colorNeutralBackground1,
			color: tokens.colorNeutralForeground1,
			borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
			":hover": {
				backgroundColor: tokens.colorNeutralBackground1Hover,
			},
			":last-child": {
				borderBottom: "none",
			},
		},
		"& .react-flow__controls-button svg": {
			fill: tokens.colorNeutralForeground1,
			width: "20px",
			height: "20px",
		},
		// Notes render behind workflow nodes
		"& .react-flow__node-note": {
			zIndex: "-1 !important" as unknown as number,
		},
		// Smooth transform transition for explicit re-layouts (Format Workflow
		// button, etc.) only. We deliberately do NOT animate transforms during
		// interactive drags — XY Flow updates `transform` on every pointermove,
		// and animating that would add visible cursor lag. The data attribute
		// is toggled on the wrapper for ~one transition duration after a
		// re-layout completes; outside that window, drags use XY Flow's native
		// (un-transitioned) transform updates.
		"&[data-otto-relayout='true'] .react-flow__node": {
			transitionProperty: "transform",
			transitionDuration: tokens.durationNormal,
			transitionTimingFunction: "cubic-bezier(0.23, 1, 0.32, 1)",
		},
		"@media (prefers-reduced-motion: reduce)": {
			"&[data-otto-relayout='true'] .react-flow__node": {
				transitionDuration: "0.01ms",
			},
		},
		// MiniMap styling - positioned to clear DockToolbar
		"& .react-flow__minimap": {
			backgroundColor: tokens.colorNeutralBackground2,
			border: 'none',
			boxShadow: tokens.shadow4,
			borderRadius: tokens.borderRadiusLarge,
			left: "16px !important",
			// Position above the dock toolbar (48px) + padding
			bottom: "70px !important",
			zIndex: 10,
			transitionProperty: 'box-shadow',
			transitionDuration: tokens.durationNormal,
			transitionTimingFunction: tokens.curveDecelerateMax,
			'@media (prefers-reduced-motion: reduce)': {
				transitionDuration: '0.01ms',
			},
			":hover": {
				boxShadow: tokens.shadow8,
			},
		},
		"& .react-flow__minimap-mask": {
			fill: tokens.colorNeutralBackgroundAlpha2,
			stroke: tokens.colorNeutralStroke1,
			strokeWidth: 1,
		},
	},
	emptyState: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		width: "100%",
		height: "100%",
	},
	emptyStateText: {
		color: tokens.colorNeutralForeground2,
		fontSize: tokens.fontSizeBase400,
	},
	// Monitoring mode styles
	loadingState: {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		width: "100%",
		height: "100%",
		gap: tokens.spacingVerticalM,
	},
	loadingText: {
		color: tokens.colorNeutralForeground2,
		fontSize: tokens.fontSizeBase400,
	},
	connectionsLoading: {
		position: 'absolute',
		top: tokens.spacingVerticalM,
		left: tokens.spacingHorizontalM,
		display: 'flex',
		alignItems: 'center',
		gap: tokens.spacingHorizontalS,
		padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
		backgroundColor: tokens.colorNeutralBackground1,
		borderRadius: tokens.borderRadiusMedium,
		boxShadow: tokens.shadow4,
		zIndex: 10,
		opacity: 0.9,
	},
	connectionsLoadingText: {
		color: tokens.colorNeutralForeground2,
		fontSize: tokens.fontSizeBase200,
	},
	containerDragOver: {
		outline: `2px dashed ${tokens.colorBrandStroke1}`,
		outlineOffset: '-2px',
		backgroundColor: tokens.colorBrandBackground2,
	},
	containerDragInvalid: {
		outline: `2px dashed ${tokens.colorPaletteRedBorder2}`,
		outlineOffset: '-2px',
		backgroundColor: tokens.colorPaletteRedBackground1,
	},
	dropFeedback: {
		position: 'absolute',
		top: '50%',
		left: '50%',
		transform: 'translate(-50%, -50%)',
		padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
		borderRadius: tokens.borderRadiusMedium,
		backgroundColor: tokens.colorPaletteRedBackground1,
		border: `1px solid ${tokens.colorPaletteRedBorder2}`,
		color: tokens.colorPaletteRedForeground1,
		fontSize: tokens.fontSizeBase300,
		fontWeight: tokens.fontWeightSemibold,
		zIndex: 1000,
		pointerEvents: 'none',
		boxShadow: tokens.shadow8,
	},
});

const nodeTypes: NodeTypes = {
	trigger: FloatingTriggerNode,
	placeholderTrigger: PlaceholderTriggerNode,
	action: FloatingActionNode,
	if: FloatingIfNode,
	switch: FloatingSwitchNode,
	foreach: FloatingForeachNode,
	agent: FloatingAgentNode,
	tool: FloatingToolNode,
	model: FloatingModelNode,
	phantom: PhantomNode,
	note: NoteNode,
	scopeContainer: ScopeContainerNode,
	merge: MergeNode,
};

const edgeTypes: EdgeTypes = {
	addButton: AddButtonEdge,
	terminal: TerminalEdge,
	dotted: DottedEdge,
};

/**
 * Panel state for operation picker
 */
type PanelState = {
	isOpen: boolean;
	mode: "trigger" | "action" | "tool";
	insertionPoint: InsertionPoint | null;
	/** For tool mode: the agent to add the tool to */
	targetAgentName?: string;
};

/**
 * Props for monitoring mode
 */
export type WorkflowDesignerMonitoringProps = {
	/** Whether the designer is in monitoring (read-only) mode */
	isMonitoringMode?: boolean;
	/** Node executions to display status overlays */
	nodeExecutions?: NodeExecution[];
	/** Currently selected node ID */
	selectedNodeId?: string | null;
	/** Callback when a node is clicked in monitoring mode */
	onNodeClick?: (nodeId: string) => void;
	/** Whether the run data is loading */
	isLoading?: boolean;
	/** Workflow definition snapshot from the run (used in monitoring mode instead of store) */
	workflowSnapshot?: { definition: Record<string, unknown>; kind?: string | undefined } | undefined;
	/** Current iteration index for agents (keyed by agent name) */
	agentIterations?: Record<string, number>;
	/** Full iteration data for agents (keyed by agent name) - used to filter tools shown on canvas */
	agentIterationsData?: Map<string, AgentIterationInfo[]> | undefined;
	/** Callback when agent iteration changes */
	onIterationChange?: (agentName: string, iteration: number) => void;
	/** Current iteration index for scope nodes (keyed by scope name) */
	scopeIterations?: Record<string, number>;
	/** Callback when scope iteration changes */
	onScopeIterationChange?: (scopeName: string, iteration: number) => void;
	/** Callback when user requests to generate a workflow from empty canvas */
	onGenerateWorkflow?: ((prompt: string) => void) | undefined;
	/** Whether the chat sidebar is open (hides textarea on empty canvas when true) */
	isChatSidebarOpen?: boolean | undefined;

	// Command bar props (for FloatingCommandBar)
	/** Whether the code panel is visible */
	isCodeVisible?: boolean;
	/** Toggle code panel visibility */
	onToggleCode?: () => void;
	/** Toggle chat sidebar */
	onToggleChat?: () => void;
	/** Open run workflow dialog */
	onRun?: () => void;
	/** Whether workflow has unsaved changes */
	isDirty?: boolean;
	/** Whether save is in progress */
	isSaving?: boolean;
	/** Trigger save */
	onSave?: () => void;
	/** Whether validation is in progress (shown during save) */
	isValidating?: boolean;
	/** Whether this is a conversational (A2A) workflow */
	isConversational?: boolean;
	/** Start a new chat (for conversational workflows) */
	onStartChat?: () => void;
	/** Container element for command bar bounds */
	commandBarContainerRef?: React.RefObject<HTMLElement | null>;
	/** Extra offset from top for command bar */
	commandBarTopOffset?: number;

	// Draft mode props
	/** Whether draft mode is enabled (shows draft badge, autosave status, mode toggle) */
	draftModeEnabled?: boolean;
	/** Workflow name (required for draft mode autosave) */
	workflowName?: string | undefined;

	// Validation props (for draft mode)
	/** Callback to trigger validation */
	onValidate?: () => void;
	/** Number of validation errors (0 = valid, undefined = not validated) */
	validationErrorCount?: number;


};

type WorkflowDesignerContentProps = WorkflowDesignerMonitoringProps;

// Stable default values - defined outside component to avoid recreating on each render
const EMPTY_NODE_EXECUTIONS: NodeExecution[] = [];
const EMPTY_AGENT_ITERATIONS: Record<string, number> = {};
const EMPTY_SCOPE_ITERATIONS: Record<string, number> = {};

/** Build a ScopeContext from graph node data when inside a loop scope */
export function buildScopeContext(
	scopeParentId: string | undefined,
	branchType: string | undefined,
): ScopeContext | undefined {
	if (!scopeParentId || branchType !== 'loop') return undefined;
	// Extract action name from scoped node ID (e.g., "action-OuterScope/ForEach_1" → "ForEach_1")
	const scopedPath = scopeParentId.replace(/^action-/, '');
	const parentName = scopedPath.includes('/') ? scopedPath.split('/').pop()! : scopedPath;
	return {
		parentActionName: parentName,
		branchType: 'loop',
		parentScopes: [{ actionName: parentName, type: 'Foreach' }],
	};
}

/**
 * Apply scope container bounds (position + dimensions) to scope container nodes.
 * Returns the original array if no containers need updating.
 */
const applyScopeContainerBounds = (nodes: GraphNode[]): GraphNode[] => {
	const boundsMap = calculateScopeContainerBounds(nodes);
	if (boundsMap.size === 0) return nodes;

	return nodes.map((node) => {
		const bounds = boundsMap.get(node.id);
		if (!bounds) return node;
		return {
			...node,
			position: bounds.position,
			data: {
				...node.data,
				containerWidth: bounds.width,
				containerHeight: bounds.height,
			},
		};
	});
};

export const WorkflowDesignerContent = ({
	isMonitoringMode = false,
	nodeExecutions,
	selectedNodeId: monitoringSelectedNodeId,
	onNodeClick: onMonitoringNodeClick,
	isLoading = false,
	workflowSnapshot,
	agentIterations,
	agentIterationsData,
	onIterationChange,
	scopeIterations,
	onScopeIterationChange,
	isChatSidebarOpen = false,
	// Command bar props
	isCodeVisible = false,
	onToggleCode,
	onToggleChat,
	onRun,
	isDirty = false,
	isSaving = false,
	onSave,
	isValidating = false,
	isConversational = false,
	onStartChat,
	commandBarContainerRef: _commandBarContainerRef,
	commandBarTopOffset: _commandBarTopOffset = 0,
	// Draft mode props
	draftModeEnabled = false,
	workflowName,
	// Validation props
	onValidate,
	validationErrorCount,
}: WorkflowDesignerContentProps) => {
	// Use stable defaults to prevent infinite re-renders from object reference changes
	const stableNodeExecutions = nodeExecutions ?? EMPTY_NODE_EXECUTIONS;
	const stableAgentIterations = agentIterations ?? EMPTY_AGENT_ITERATIONS;
	const stableScopeIterations = scopeIterations ?? EMPTY_SCOPE_ITERATIONS;
	const isDraftReadOnly = useDraftModeStore(selectIsReadOnly);
	const isReadOnlyCanvas = isMonitoringMode || isDraftReadOnly;

	const { data: apiConnections, isLoading: isLoadingConnections } = useConnections();
	const validApiConnections = useMemo(() => getValidConnections(apiConnections ?? []), [apiConnections]);

	const styles = useStyles();
	const {
		workflow: storeWorkflow,
		currentWorkflowIdentity,
		currentMode,
		updateActionPosition,
		updateTriggerPosition,
		updateToolNodePosition,
		addEdge,
		removeEdge,
		layoutOrientation,
		addTrigger,
		addAction,
		addToolToAgent,
		duplicateAction,
		pasteActionFromClipboard,
		deleteAction,
		deleteTrigger,
		deleteToolFromAgent,
		generateUniqueActionName,
		generateUniqueToolName,
		selectNode,
		selectNodeByName,
		selectTool,
		getTool,
		getAction,
		clearSelection,
		toggleNodeSelection,
		openNodeEditor,
		focusNode,
		selectedNode,
		selectedNodes,
		isLayouting,
		setIsLayouting,
		applyLayoutPositions,
		selectNodes,
		addNodesToSelection,
	} = useWorkflowStore();
	const notes = useWorkflowStore((s) => s.workflow?.notes);
	const addNote = useWorkflowStore((s) => s.addNote);
	const updateNote = useWorkflowStore((s) => s.updateNote);
	const deleteNote = useWorkflowStore((s) => s.deleteNote);
	const isNodeEditorOpen = useNodeEditorStore((s) => s.isOpen);
	const closeNodeEditor = useNodeEditorStore((s) => s.closeEditor);
	
	// Clipboard store for copy/paste operations
	const { copyAction, clipboardData, clipboardActionName, setCurrentWorkflow } = useClipboardStore(
		useShallow((s) => ({
			copyAction: s.copyAction,
			clipboardData: s.clipboardData,
			clipboardActionName: s.clipboardActionName,
			setCurrentWorkflow: s.setCurrentWorkflow,
		}))
	);
	
	// In monitoring mode, layout positions are computed locally instead of
	// being persisted on the store workflow. The snapshot returned by the run
	// API strips metadata.position, so we run the auto-layout below and store
	// the result here. We never mutate the editable store workflow from
	// monitoring mode (it belongs to a different — possibly newer — definition).
	const [monitoringPositions, setMonitoringPositions] = useState<Map<string, Position> | null>(null);

	// In monitoring mode with a workflow snapshot, use the snapshot instead of store
	// This ensures we show the exact workflow that was executed for this run
	// (the live definition may have been edited since this run executed).
	const workflow = useMemo((): WorkflowDefinition | null => {
		if (isMonitoringMode && workflowSnapshot?.definition) {
			const baseDef = workflowSnapshot.definition as WorkflowDefinition['definition'];
			const kind = workflowSnapshot.kind ?? storeWorkflow?.kind ?? 'Stateful';

			// In monitoring, layout is always recomputed on the frontend so every
			// run renders identically regardless of which version executed (draft
			// runs carry user-authored metadata.position; production runs do not).
			// Strip incoming positions and, when available, inject the FE-computed
			// monitoringPositions. This guarantees the auto-layout effect always
			// runs for a fresh snapshot, and the displayed graph depends only on
			// the workflow's structural definition.
			const stripPositionFromActions = (
				actions: Record<string, Action> | undefined,
			): Record<string, Action> | undefined => {
				if (!actions) return actions;
				const out: Record<string, Action> = {};
				for (const [name, action] of Object.entries(actions)) {
					const pos = monitoringPositions?.get(`action-${name}`);
					if (pos) {
						out[name] = { ...action, metadata: { ...action.metadata, position: pos } };
					} else if (action.metadata?.position) {
						// Strip pre-existing position so useWorkflowGraph treats the
						// node as un-positioned and triggers the auto-layout pass.
						const { position: _drop, ...metaRest } = action.metadata;
						out[name] = { ...action, metadata: metaRest };
					} else {
						out[name] = action;
					}
				}
				return out;
			};
			const triggersOut: typeof baseDef.triggers = {};
			for (const [name, trigger] of Object.entries(baseDef.triggers ?? {})) {
				const pos = monitoringPositions?.get(`trigger-${name}`);
				if (pos) {
					triggersOut[name] = { ...trigger, metadata: { ...trigger.metadata, position: pos } };
				} else if (trigger.metadata?.position) {
					const { position: _drop, ...metaRest } = trigger.metadata;
					triggersOut[name] = { ...trigger, metadata: metaRest };
				} else {
					triggersOut[name] = trigger;
				}
			}
			return {
				definition: {
					...baseDef,
					triggers: triggersOut,
					actions: stripPositionFromActions(baseDef.actions) ?? baseDef.actions,
				},
				kind,
			};
		}
		return storeWorkflow;
	}, [isMonitoringMode, workflowSnapshot, storeWorkflow, monitoringPositions]);

	// Drop cached monitoring positions on snapshot identity changes (new run
	// selected) AND when leaving monitoring mode. Also resets the auto-layout
	// fingerprint guard so a new snapshot triggers a fresh layout pass.
	// (lastLayoutedWorkflowRef is declared later — captured lazily by this
	// effect's callback, which always runs after render commit.)
	const lastMonitoringSnapshotRef = useRef<typeof workflowSnapshot>(workflowSnapshot);
	useEffect(() => {
		if (!isMonitoringMode) {
			if (monitoringPositions !== null) {
				setMonitoringPositions(null);
			}
			lastMonitoringSnapshotRef.current = undefined;
			// Also clear the auto-layout fingerprint so design mode re-runs
			// layout when the editable workflow happens to share the same
			// trigger/action keys as the last viewed run snapshot.
			lastLayoutedWorkflowRef.current = null;
			return;
		}
		if (lastMonitoringSnapshotRef.current !== workflowSnapshot) {
			lastMonitoringSnapshotRef.current = workflowSnapshot;
			setMonitoringPositions(null);
			lastLayoutedWorkflowRef.current = null;
		}
	}, [isMonitoringMode, workflowSnapshot, monitoringPositions]);
	// Get connection references for defaulting connections on new nodes
	const connectionReferences = useConnectionStore((state) => state.references);

	// In monitoring mode, positions are snapshot-local: convert the Map into
	// the override map expected by `useWorkflowGraph` so the snapshot bypasses
	// the design-mode layout store entirely. Pass an empty map (rather than
	// undefined) before the snapshot has been laid out so the hook doesn't
	// fall back to the design-mode layout store, which would carry positions
	// for a structurally-different definition.
	const monitoringPositionsOverride = useMemo(() => {
		if (!isMonitoringMode) return undefined;
		const out: Record<string, Position> = {};
		monitoringPositions?.forEach((v, k) => { out[k] = v; });
		return out;
	}, [isMonitoringMode, monitoringPositions]);

	const { nodes: graphNodes, edges: graphEdges } = useWorkflowGraph(workflow, monitoringPositionsOverride);
	const { screenToFlowPosition, fitView, getNodes } = useReactFlow();
	const updateNodeInternals = useUpdateNodeInternals();

	// Refs to track latest graph data - prevents stale closure issues in callbacks
	const graphNodesRef = useRef(graphNodes);
	graphNodesRef.current = graphNodes;
	const graphEdgesRef = useRef(graphEdges);
	graphEdgesRef.current = graphEdges;
	// Track previous graphNodes identity to detect workflow data changes
	const prevGraphNodesRef = useRef(graphNodes);

	// Get streaming state from chat store (for showing skeleton while assistant is working)
	const isStreaming = useAgentChatAguiStore((state) => state.isStreaming);
	const currentStatus = useAgentChatAguiStore((state) => state.currentStatus);
	const chatMessages = useAgentChatAguiStore((state) => state.chatMessages);
	const setEditScope = useAgentChatAguiStore((state) => state.setEditScope);

	// Debug mode - detect dirty nodes (changed since execution was captured)
	const isExecutionMode = useEditorRunStore((state) => state.isExecutionMode);
	const detectDirtyNodes = useEditorRunStore((state) => state.detectDirtyNodes);

	// Compute dirty node IDs when in execution mode
	const dirtyNodeIds = useMemo(() => {
		if (!isExecutionMode || !workflow) return new Set<string>();
		const result = detectDirtyNodes(workflow);
		return result.dirtyNodeIds;
	}, [isExecutionMode, detectDirtyNodes, workflow]);

	// Get the first user message to show as the prompt during generation
	const firstUserMessage = chatMessages.find((m) => m.role === 'user')?.content;

	// Preload operations and connectors for better browse performance (design mode only)
	// Note: Hook must be called unconditionally per React rules, but underlying queries
	// are already conditional on having a selected app
	usePreloadOperations();

	// Connector swagger fetching needs app context + auth
	const selectedApp = useAppStore((state) => state.selectedApp);
	const { getOttoToken } = useAuth();
	// Imperative connector-swagger fetches (e.g. resolving a managed MCP
	// server path on pick) go through this cache so they dedupe against any
	// `useConnectorSwagger` subscriptions for the same connector id.
	const queryClient = useQueryClient();

	// Track shift key state for box selection (to add to selection instead of replacing)
	const isShiftKeyDown = useRef(false);

	// Set up global keyboard listeners for shift key tracking
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Shift') {
				isShiftKeyDown.current = true;
			}
		};
		const handleKeyUp = (e: KeyboardEvent) => {
			if (e.key === 'Shift') {
				isShiftKeyDown.current = false;
			}
		};
		window.addEventListener('keydown', handleKeyDown);
		window.addEventListener('keyup', handleKeyUp);
		return () => {
			window.removeEventListener('keydown', handleKeyDown);
			window.removeEventListener('keyup', handleKeyUp);
		};
	}, []);

	// Update clipboard store when workflow changes (to auto-clear on workflow switch)
	useEffect(() => {
		setCurrentWorkflow(workflowName ?? null);
	}, [workflowName, setCurrentWorkflow]);

	// Clear any pending data-otto-relayout cleanup on unmount.
	useEffect(() => {
		return () => {
			if (relayoutClearTimerRef.current != null) {
				window.clearTimeout(relayoutClearTimerRef.current);
				relayoutClearTimerRef.current = null;
			}
		};
	}, []);

	// Panel state
	const [panelState, setPanelState] = useState<PanelState>({
		isOpen: false,
		mode: "action",
		insertionPoint: null,
	});

	// Tool type menu state (shown when clicking Add Tool on agent)
	const [toolTypeMenuState, setToolTypeMenuState] = useState<{
		open: boolean;
		targetAgentName: string | null;
		anchorEl: HTMLElement | null;
	}>({ open: false, targetAgentName: null, anchorEl: null });

	// Auxiliary picker view (MCP / sub-workflow / agent-type). Mutually
	// exclusive with `panelState` — opening one closes the other — so the
	// `PickerPanelHost` always has a single panel to render and views swap
	// in place rather than stacking.
	type AuxPickerView =
		| { kind: 'mcp'; targetAgentName: string | null }
		| { kind: 'subworkflow'; targetAgentName: string | null }
		| {
				kind: 'agent-type';
				insertionPoint: { sourceNodeId: string; targetNodeId?: string } | null;
		  };
	const [auxPickerView, setAuxPickerView] = useState<AuxPickerView | null>(null);

	// Canvas search state
	const [isSearchOpen, setIsSearchOpen] = useState(false);

	// User-toggled "lock" from the canvas controls. When true, blocks node
	// dragging, edge creation, and element selection. Independent of
	// `isReadOnlyCanvas` (which is mode-driven for monitoring/published) and
	// persisted via `workflowViewPrefsStore` so the choice survives reloads.
	const isCanvasLocked = useIsCanvasLocked();
	const handleToggleCanvasLock = useToggleCanvasLock();

	// Dismiss edit panels when canvas becomes read-only (published/monitoring mode)
	useEffect(() => {
		if (!isReadOnlyCanvas) return;
		setPanelState({ isOpen: false, mode: "action", insertionPoint: null });
		setAuxPickerView(null);
		setToolTypeMenuState({ open: false, targetAgentName: null, anchorEl: null });
	}, [isReadOnlyCanvas]);

	// Mutual exclusivity: opening the operations picker closes any auxiliary
	// picker so the unified `PickerPanelHost` shows the new view in place
	// instead of stacking another panel below it.
	useEffect(() => {
		if (panelState.isOpen) setAuxPickerView(null);
	}, [panelState.isOpen]);

	// Template gallery panel state
	const [isTemplateGalleryOpen, setIsTemplateGalleryOpen] = useState(false);
	const [initialPreviewTemplateId, setInitialPreviewTemplateId] = useState<string | undefined>(undefined);

	// React Router search params — used to read & clean up query parameters
	const [searchParams, setSearchParams] = useSearchParams();
	const isWorkflowTemplatesEnabled = useFeatureFlag('workflowTemplates');

	// Template auto-apply: when arriving from the dashboard "Build from templates"
	// flow, the URL carries `?applyTemplate=<id>` on its own (no openTemplates flag).
	// We defer the apply until the workflow has loaded into the store; otherwise
	// applyTemplate would have nothing to patch.
	const hasTemplateQueryParams = useMemo(
		() => searchParams.has('openTemplates') || searchParams.has('applyTemplate'),
		[searchParams]
	);
	const { getTemplate: getTemplateById } = useTemplates({
		enabled: isWorkflowTemplatesEnabled && hasTemplateQueryParams,
	});
	const { applyTemplate: applyTemplateToCanvas } = useTemplateApply();
	const autoApplyAttemptedRef = useRef(false);

	useEffect(() => {
		if (!hasTemplateQueryParams) return;
		if (!isWorkflowTemplatesEnabled) return;

		const openTemplates = searchParams.get('openTemplates') === 'true';
		const templateIdParam = searchParams.get('applyTemplate');
		// Path A — gallery open (optionally with a specific preview).
		if (openTemplates) {
			setIsTemplateGalleryOpen(true);
			if (templateIdParam) {
				setInitialPreviewTemplateId(templateIdParam);
			}
			setSearchParams((prev) => {
				const next = new URLSearchParams(prev);
				next.delete('openTemplates');
				next.delete('applyTemplate');
				return next;
			}, { replace: true });
			return;
		}

		// Path B — auto-apply (used by the dashboard create-from-template flow).
		// Wait until both the workflow has loaded AND the workflow identity is
		// populated. The identity is gated on MSAL `account` being ready, so it
		// can land *after* the workflow definition; if we apply earlier, the
		// `#workflowname#` rewrite has no name to substitute and produces a
		// workflow with stripped placeholders. The ref-guard below blocks any
		// retry, so we must wait for both inputs the first time around.
		if (!templateIdParam || autoApplyAttemptedRef.current) return;
		if (!storeWorkflow || !currentWorkflowIdentity?.workflow) return;

		autoApplyAttemptedRef.current = true;

		const template = getTemplateById(templateIdParam);
		if (template) {
			// Templates apply immediately now — unresolved connections get
			// placeholder entries the user can fix via the Connections dialog.
			applyTemplateToCanvas(template, { silent: true });
		}

		setSearchParams((prev) => {
			const next = new URLSearchParams(prev);
			next.delete('openTemplates');
			next.delete('applyTemplate');
			return next;
		}, { replace: true });
	}, [
		searchParams,
		setSearchParams,
		hasTemplateQueryParams,
		isWorkflowTemplatesEnabled,
		storeWorkflow,
		currentWorkflowIdentity,
		getTemplateById,
		applyTemplateToCanvas,
	]);

	// Check if workflow has any triggers
	const hasTriggers =
		!!workflow && Object.keys(workflow.definition.triggers || {}).length > 0;

	// Check if workflow has any actions
	const hasActions =
		!!workflow && Object.keys(workflow.definition.actions || {}).length > 0;

	// Quick reference to workflow actions for ForEach constraint checks
	const workflowActions = workflow?.definition?.actions;

	// Canvas is empty if no triggers and no actions
	const isCanvasEmpty = !hasTriggers && !hasActions;

	// Track which agents have their tools expanded
	// In designer mode: expanded by default (show all tools)
	// In monitoring mode: collapsed by default (show only invoked tools)
	const [expandedAgents, setExpandedAgents] = useState<Set<string>>(() => {
		if (isMonitoringMode) {
			// Monitoring mode: start collapsed
			return new Set<string>();
		}
		// Designer mode: expand all agents with tools
		const initialExpanded = new Set<string>();
		graphNodes.forEach((node) => {
			if (node.type === 'agent' && node.data.tools && Object.keys(node.data.tools).length > 0) {
				initialExpanded.add(node.data.actionName);
			}
		});
		return initialExpanded;
	});

	// Reset expanded state when entering/exiting monitoring mode
	// In monitoring mode: collapse all (show only invoked tools by default)
	// In designer mode: expand all agents with tools
	useEffect(() => {
		if (isMonitoringMode) {
			setExpandedAgents(new Set<string>());
		} else {
			const expanded = new Set<string>();
			graphNodes.forEach((node) => {
				if (node.type === 'agent' && node.data.tools && Object.keys(node.data.tools).length > 0) {
					expanded.add(node.data.actionName);
				}
			});
			setExpandedAgents(expanded);
		}
	}, [isMonitoringMode, graphNodes]);

	// Track which sub-workflow is expanded (max 1 at a time, accordion style)
	const [expandedSubWorkflow, setExpandedSubWorkflow] = useState<string | null>(null);

	const handleSubWorkflowExpandToggle = useCallback(
		(toolName: string, expanded: boolean) => {
			setExpandedSubWorkflow(expanded ? toolName : null);
		},
		[],
	);

	// Handler for toggling agent tools expansion
	const handleAgentToolsExpandToggle = useCallback(
		(agentName: string, expanded: boolean) => {
			setExpandedAgents((prev) => {
				const next = new Set(prev);
				if (expanded) {
					next.add(agentName);
				} else {
					next.delete(agentName);
				}
				return next;
			});
		},
		[],
	);

	// Handler for adding a tool to an agent - shows tool type menu
	const handleAddToolClick = useCallback(
		(agentName: string, anchorEl: HTMLElement) => {
			setToolTypeMenuState({
				open: true,
				targetAgentName: agentName,
				anchorEl,
			});
		},
		[],
	);

	// Bridge: the agent's parameter panel (in a sibling subtree) dispatches
	// `agent-add-tool` instead of prop-drilling through the node-editor store.
	useEffect(() => {
		const handler = (e: Event) => {
			const detail = (e as CustomEvent<AgentAddToolEventDetail>).detail;
			if (!detail?.agentName || !detail?.anchor) return;
			handleAddToolClick(detail.agentName, detail.anchor);
		};
		window.addEventListener(AGENT_ADD_TOOL_EVENT, handler);
		return () => window.removeEventListener(AGENT_ADD_TOOL_EVENT, handler);
	}, [handleAddToolClick]);

	// Handler for tool type selection from menu
	const handleToolTypeSelect = useCallback(
		(type: ToolType) => {
			const agentName = toolTypeMenuState.targetAgentName;
			setToolTypeMenuState({ open: false, targetAgentName: null, anchorEl: null });

			if (!agentName) return;

			// Close the agent's node editor so the picker side panel (operations /
			// MCP / sub-workflow) isn't visually covered by the modal. The picker
			// flow tracks its own targetAgentName, so it's safe to clear selection.
			void closeNodeEditor();

			if (type === 'action') {
				// Open existing operation picker panel for action tools
				setAuxPickerView(null);
				setPanelState({
					isOpen: true,
					mode: "tool",
					insertionPoint: null,
					targetAgentName: agentName,
				});
			} else if (type === 'mcp') {
				setPanelState(prev => ({ ...prev, isOpen: false }));
				setAuxPickerView({ kind: 'mcp', targetAgentName: agentName });
			} else if (type === 'subworkflow') {
				setPanelState(prev => ({ ...prev, isOpen: false }));
				setAuxPickerView({ kind: 'subworkflow', targetAgentName: agentName });
			}
		},
		[toolTypeMenuState.targetAgentName, closeNodeEditor],
	);

	// Handler for closing tool type menu
	const handleToolTypeMenuClose = useCallback(() => {
		setToolTypeMenuState({ open: false, targetAgentName: null, anchorEl: null });
	}, []);

	// Handler for MCP server selection.
	//
	// We model an MCP pick at the *server* level (not per-tool). The created
	// `McpClientTool` represents the connection to the server; allowed-tool
	// configuration happens after selection via the MCP config tab in the
	// node editor (Phase 6). Omitting `parameters.allowedTools` means "all
	// tools exposed by the server are allowed" — this matches the runtime
	// contract in `Flow.Data.Common/Engines/FlowRunEngine.cs` (reads
	// `Parameters?.AllowedTools`; absence = unrestricted).
	//
	// - BYO (`source === 'byo'`): `serverConfig.connectionId` is the
	//   `connections.json.agentMcpConnections` record key. That key IS the
	//   runtime's `connectionReference.connectionName`. Kind = `'Builtin'`.
	// - Managed (`source === 'managed'`): the tool is added to canvas WITHOUT
	//   a connection (mirroring Otto's existing managed-API flow). We resolve
	//   the runtime `mcpServerPath` from the connector swagger up front and
	//   carry the project-scoped `connectorId` in `connectionReference.api.id`
	//   so the node-editor's MCP config tab can create the managed connection
	//   and run list-tools later. Kind = `'Managed'`.
	const handleMCPServerSelect = useCallback(
		async (serverConfig: MCPServerConfig, source: 'managed' | 'byo') => {
			let agentName =
				auxPickerView?.kind === 'mcp' ? auxPickerView.targetAgentName : null;

			// Resolve the managed runtime path before building the tool. BYO
			// servers need no path (the runtime resolves them from the BYO
			// connection record).
			let managedServerPath: string | null = null;
			if (source === 'managed') {
				const connectorId = serverConfig.connectorId;
				const operationId = serverConfig.operationId;
				if (!connectorId || !operationId || !selectedApp?.projectName) {
					console.warn(
						'[WorkflowDesigner] Managed MCP server is missing connectorId/operationId/project; ignoring pick.',
						serverConfig,
					);
					setAuxPickerView(null);
					return;
				}
				try {
					const { parser } = await queryClient.fetchQuery({
						queryKey: connectorSwaggerQueryKey(connectorId),
						queryFn: async () => {
							const token = (await getOttoToken()) ?? '';
							return fetchConnectorSwaggerResult(
								selectedApp.projectName,
								token,
								connectorId,
							);
						},
						staleTime: CONNECTOR_SWAGGER_STALE_TIME,
					});
					// The control-plane operationId is project-prefixed
					// (e.g. "<project>/<connector>/InvokeMCP"); the swagger's
					// operationId is just the last segment. Try as-is first,
					// then fall back to the trailing segment.
					managedServerPath =
						parser.getMcpServerPath(operationId) ??
						parser.getMcpServerPath(operationId.split('/').pop() ?? '');
				} catch (err) {
					console.warn(
						'[WorkflowDesigner] Failed to resolve managed MCP server path.',
						err,
					);
				}

				// A managed MCP tool is only addressable at runtime via
				// `inputs.parameters.mcpServerPath`. If we could not resolve it
				// (swagger fetch failed, or the operation has no MCP path), adding
				// the tool would create a node that is broken at execution time.
				// Block the add and surface a clear error instead of persisting a
				// half-built tool.
				if (!managedServerPath) {
					useNotificationStore.getState().addNotification({
						type: 'error',
						title: 'Could not add MCP server',
						message: `Couldn't resolve the MCP server path for "${serverConfig.name}". Check your connection to the connector and try again.`,
					});
					setAuxPickerView(null);
					return;
				}
			}

			const buildMcpTool = (): AgentTool => {
				const isManaged = source === 'managed';
				const connectionReference: NonNullable<
					AgentTool['inputs']
				>['connectionReference'] = {
					// Managed connections are created later in the node editor,
					// so the reference starts empty for managed servers.
					connectionName: isManaged ? '' : serverConfig.connectionId ?? '',
				};
				// Carry the project-scoped connector id so the MCP config tab can
				// drive connection creation (mirrors the ConnectionReference shape).
				if (isManaged && serverConfig.connectorId) {
					connectionReference.api = { id: serverConfig.connectorId };
				}

				const parameters: Record<string, unknown> = {};
				if (isManaged && managedServerPath) {
					parameters.mcpServerPath = managedServerPath;
				}

				const inputs: NonNullable<AgentTool['inputs']> = {
					connectionReference,
					parameters,
				};
				const tool: AgentTool = {
					type: 'McpClientTool',
					kind: isManaged ? 'Managed' : 'Builtin',
					inputs,
				};
				if (serverConfig.description) {
					tool.description = serverConfig.description;
				}
				return tool;
			};

			// Server display name → slugified map key (tools are keyed by name
			// in `agent.tools`; the canvas/editor re-humanizes for display).
			const slugifyName = (raw: string): string =>
				raw.replace(/[^a-zA-Z0-9_]+/g, '_').replace(/^_+|_+$/g, '') || 'McpServer';
			const baseName = slugifyName(serverConfig.name);

			// If no target agent, create a new agent with this tool
			if (!agentName) {
				agentName = generateUniqueActionName('Agent_Loop');
				const tool = buildMcpTool();

				const agentAction: Action = {
					type: 'Agent',
					metadata: { position: { x: 250, y: 150 } },
					tools: {
						[baseName]: tool,
					},
				};

				addAction(agentName, agentAction);
				selectTool(agentName, baseName);
				setAuxPickerView(null);
				return;
			}

			// Adding to existing agent
			const toolName = generateUniqueToolName(agentName, baseName);
			const tool = buildMcpTool();

			addToolToAgent(agentName, toolName, tool);
			selectTool(agentName, toolName);
			setAuxPickerView(null);
		},
		[
			auxPickerView,
			addToolToAgent,
			selectTool,
			generateUniqueToolName,
			generateUniqueActionName,
			addAction,
			selectedApp?.projectName,
			getOttoToken,
			queryClient,
		],
	);

	// Handler for subworkflow selection
	// Uses the same code path as handleOperationSelect for tool mode
	// This ensures subworkflow tools are created with the same structure as any other action tool
	const handleSubworkflowSelect = useCallback(
		(selectedWorkflow: CallableWorkflow) => {
			const agentName =
				auxPickerView?.kind === 'subworkflow' ? auxPickerView.targetAgentName : null;

			const workflowName = selectedWorkflow.name;

			// Create an OperationDefinition for the workflow invocation
			// This follows the same pattern as FUNCTION_AND_WORKFLOW_OPERATIONS.invoke_workflow
			// but with the workflow ID pre-populated
			const workflowOperation: OperationDefinition = {
				id: 'invoke_workflow',
				name: `Call ${workflowName}`,
				type: 'Workflow',
				category: 'action',
				description: `Invoke workflow: ${workflowName}`,
				brandColor: '#0078D4',
				defaultInputs: {
					host: {
						workflow: {
							id: workflowName,
						},
					},
					body: {},
				},
			};

			if (!agentName) {
				// Standalone invocation from toolbar — add as a canvas action
				const inputs = buildConnectorInputs(workflowOperation, validApiConnections, connectionReferences);
				const actionName = generateUniqueActionName(`Call_${workflowName}`);
				const floatingPosition = calculateFloatingNodePosition(
					graphNodes.map(n => n.position),
					layoutOrientation === 'horizontal',
				);
				const action: Action = {
					type: workflowOperation.type,
					inputs,
					metadata: { position: floatingPosition },
				};
				addAction(actionName, action);
				openNodeEditor({ id: `action-${actionName}`, name: actionName, type: 'action' });
				setAuxPickerView(null);
				return;
			}

			// Generate unique tool name based on workflow name
			const toolName = generateUniqueToolName(agentName, workflowName);

			// Use the same code path as handleOperationSelect for tool mode:
			// Build inputs using buildConnectorInputs (handles Workflow type)
			const inputs = buildConnectorInputs(workflowOperation, validApiConnections, connectionReferences);

			// Create the inner action (same as handleOperationSelect)
			const innerAction: Action = {
				type: workflowOperation.type,
				inputs,
			};

			// Create the tool wrapper (same structure as any other action tool)
			const tool: AgentTool = {
				description: `Invoke ${workflowName} workflow`,
				actions: {
					[`${toolName}_Action`]: innerAction,
				},
			};

			// Add the tool to the agent
			addToolToAgent(agentName, toolName, tool);
			selectTool(agentName, toolName);
			setAuxPickerView(null);
		},
		[auxPickerView, addToolToAgent, selectTool, validApiConnections, generateUniqueToolName, generateUniqueActionName, addAction, openNodeEditor, graphNodes, layoutOrientation, connectionReferences],
	);

	// Handler for configuring a tool (opens the parameter panel)
	const handleToolConfigure = useCallback(
		(toolName: string, agentName: string) => {
			selectTool(agentName, toolName);
		},
		[selectTool],
	);

	// Handler for duplicating a tool
	const handleToolDuplicate = useCallback(
		(toolName: string, agentName: string) => {
			const existingTool = getTool(agentName, toolName);
			if (!existingTool) return;

			// Generate a unique name for the duplicated tool
			const newToolName = generateUniqueToolName(agentName, toolName);

			// Deep copy the tool
			const duplicatedTool = JSON.parse(JSON.stringify(existingTool));

			// Update inner action names to be unique (only if the source tool actually
			// had an `actions` branch — MCP tools don't, and injecting `actions: {}` on
			// them would pollute the serialized shape).
			if (duplicatedTool.actions !== undefined) {
				const updatedActions: Record<string, Action> = {};
				Object.entries(duplicatedTool.actions).forEach(([actionName, action]) => {
					// Generate new action name by appending a number
					const newActionName = generateUniqueActionName(actionName);
					updatedActions[newActionName] = action as Action;
				});
				duplicatedTool.actions = updatedActions;
			}

			// Add the duplicated tool to the agent
			addToolToAgent(agentName, newToolName, duplicatedTool);

			// Select the new tool
			selectTool(agentName, newToolName);
		},
		[getTool, generateUniqueToolName, generateUniqueActionName, addToolToAgent, selectTool],
	);

	// Auto-expand agents with tools when workflow changes
	useEffect(() => {
		const agentsWithTools = new Set<string>();
		graphNodes.forEach((node) => {
			if (node.type === 'agent' && node.data.tools && Object.keys(node.data.tools).length > 0) {
				agentsWithTools.add(node.data.actionName);
			}
		});
		// Add any new agents with tools to the expanded set
		// Only update if there are actually new agents to add
		setExpandedAgents((prev) => {
			let hasNewAgents = false;
			for (const agent of agentsWithTools) {
				if (!prev.has(agent)) {
					hasNewAgents = true;
					break;
				}
			}

			// If no new agents, return the same Set to avoid triggering re-renders
			if (!hasNewAgents) {
				return prev;
			}

			const next = new Set(prev);
			agentsWithTools.forEach((agent) => next.add(agent));
			return next;
		});
	}, [graphNodes]);

	// Convert notes from the store into React Flow nodes
	const noteNodes = useMemo((): Node[] => {
		if (!notes) return [];
		return Object.entries(notes).map(([id, note]) => ({
			id: `note-${id}`,
			type: 'note' as const,
			position: note.metadata.position,
			dragHandle: '.note-drag-handle',
			data: {},
			style: { width: note.metadata.width, height: note.metadata.height },
		}));
	}, [notes]);

	// Merge workflow graph nodes with note nodes
	const allNodes = useMemo(
		() => [...graphNodes, ...noteNodes] as GraphNode[],
		[graphNodes, noteNodes],
	);

	const [nodes, setNodes, onNodesChange] = useNodesState(allNodes);
	const [edges, setEdges, onEdgesChange] = useEdgesState(graphEdges);

	// NOTE: We do NOT sync allNodes directly to ReactFlow state here.
	// The WorkflowDesigner has a complex effect that computes final nodes including:
	// - Tool nodes (from expanded agents)
	// - Model nodes
	// - Terminal nodes
	// - Placeholder trigger nodes
	// That effect (around line 1900+) handles the full node computation and sync.
	// Adding a simple sync here would overwrite those dynamically computed nodes.

	// Keep nodesRef in sync with nodes
	useEffect(() => {
		nodesRef.current = nodes;
	}, [nodes]);

	// Sync store's selectedNodes with ReactFlow's internal node selection state
	// This ensures the blue borders persist when our store has nodes selected
	useEffect(() => {
		const selectedNodeIds = new Set(selectedNodes.map(n => n.id));
		setNodes((currentNodes) =>
			currentNodes.map((node) => {
				const shouldBeSelected = selectedNodeIds.has(node.id);
				if (node.selected !== shouldBeSelected) {
					return { ...node, selected: shouldBeSelected };
				}
				return node;
			})
		);
	}, [selectedNodes, setNodes]);
	const [clickPosition, setClickPosition] = useState<Position | null>(null);
	const [isContextMenuOpen, setIsContextMenuOpen] = useState(false);
	const [contextMenuPosition, setContextMenuPosition] = useState<{
		x: number;
		y: number;
	} | null>(null);

	// Node context menu state
	const [isNodeContextMenuOpen, setIsNodeContextMenuOpen] = useState(false);
	const [nodeContextMenuPosition, setNodeContextMenuPosition] = useState<{
		x: number;
		y: number;
	} | null>(null);
	const [contextMenuNodeId, setContextMenuNodeId] = useState<string | null>(null);
	const [contextMenuNodeType, setContextMenuNodeType] = useState<'trigger' | 'action' | 'tool' | null>(null);

	// Note context menu state
	const [isNoteContextMenuOpen, setIsNoteContextMenuOpen] = useState(false);
	const [noteContextMenuPosition, setNoteContextMenuPosition] = useState<{
		x: number;
		y: number;
	} | null>(null);
	const [contextMenuNoteId, setContextMenuNoteId] = useState<string | null>(null);
	const [contextMenuNoteColor, setContextMenuNoteColor] = useState<string | null>(null);

	// Edge context menu state
	const [isEdgeContextMenuOpen, setIsEdgeContextMenuOpen] = useState(false);
	const [edgeContextMenuPosition, setEdgeContextMenuPosition] = useState<{
		x: number;
		y: number;
	} | null>(null);
	const [contextMenuEdgeId, setContextMenuEdgeId] = useState<string | null>(null);
	/** Source node ID of the edge being right-clicked (for parallel branch creation) */
	const [contextMenuEdgeSourceId, setContextMenuEdgeSourceId] = useState<string | null>(null);
	/** Whether the right-clicked edge is a scope/dotted edge */
	const [contextMenuEdgeIsScopeEdge, setContextMenuEdgeIsScopeEdge] = useState(false);
	/** Source handle id of the right-clicked edge (e.g., 'true', 'false', 'loop', 'case-X') */
	const [contextMenuEdgeSourceHandle, setContextMenuEdgeSourceHandle] = useState<string | null>(null);

	// Track workflow ID for initial layout - apply layout when workflow changes
	const lastLayoutedWorkflowRef = useRef<string | null>(null);

	// Track the latest workflowSnapshot identity. Used by the async monitoring
	// layout effect to detect stale resolutions when the user switches runs
	// while calculateLayout is in flight.
	const latestSnapshotRef = useRef(workflowSnapshot);
	useEffect(() => {
		latestSnapshotRef.current = workflowSnapshot;
	}, [workflowSnapshot]);

	// Hide canvas until the first layout pass completes to prevent the brief
	// flash of nodes in a flat row (lazy positions) before Sugiyama kicks in.
	const [isInitialLayoutPending, setIsInitialLayoutPending] = useState(true);

	// Timer ref for fitView delay — cleared on unmount to avoid acting on stale state
	const fitViewTimerRef = useRef<number | null>(null);

	// Track if we're currently dragging to avoid syncing during drag
	const isDraggingRef = useRef(false);
	// Track post-drag settling period to avoid race conditions with edge updates
	// When drag ends, we need to wait for state to settle before syncing
	const postDragSettlingRef = useRef(false);

	// Track the last known "good" edge count to prevent suspicious clearing
	// This helps detect when edges are being set to empty unexpectedly
	const lastKnownEdgeCountRef = useRef(0);

	// Store current nodes in a ref to avoid recreation of handleNodesChange
	// This allows access to measured dimensions without adding `nodes` to dependencies
	const nodesRef = useRef<GraphNode[]>(nodes);

	// Ref for the empty canvas wrapper (used for FloatingCommandBar positioning)
	const emptyCanvasWrapperRef = useRef<HTMLDivElement>(null);

	// Ref for the main canvas container (used for FloatingCommandBar positioning when commandBarContainerRef is not provided)
	const canvasContainerRef = useRef<HTMLDivElement>(null);

	// Pending timeout that clears `data-otto-relayout` on the canvas container
	// after a Format Workflow re-layout. Tracked so back-to-back relayouts
	// can cancel any prior pending clear (otherwise the first timeout could
	// strip the attribute mid-second-animation).
	const relayoutClearTimerRef = useRef<number | null>(null);

	// Helper to close all context menus
	const closeAllContextMenus = useCallback(() => {
		setIsContextMenuOpen(false);
		setIsNodeContextMenuOpen(false);
		setIsNoteContextMenuOpen(false);
		setIsEdgeContextMenuOpen(false);
	}, []);

	// Create execution lookup map for monitoring mode or debug mode (when nodeExecutions provided)
	// Indexes by both nodeId (e.g., "action-Compose_1") AND nodeName-based key
	// so that canvas nodes with scoped IDs (e.g., "action-IsApproved/Compose_1" for actions
	// inside If/Switch/Foreach branches) can still find their execution data.
	const executionLookup = useMemo(() => {
		if (stableNodeExecutions.length === 0) {
			return new Map<string, NodeExecution>();
		}
		const map = new Map<string, NodeExecution>();
		stableNodeExecutions.forEach((exec) => {
			map.set(exec.nodeId, exec);
			// Also index by simple action-{nodeName} so lookups for scoped canvas node IDs
			// (e.g., "action-Parent/Child") can fall back to "action-Child".
			// Only needed for actions (triggers don't have scoped IDs).
			if (exec.nodeId.startsWith('action-')) {
				const nameKey = `action-${exec.nodeName}`;
				if (nameKey !== exec.nodeId && !map.has(nameKey)) {
					map.set(nameKey, exec);
				}
			}
		});
		return map;
	}, [stableNodeExecutions]);

	// Whether we have execution data to display (monitoring mode OR debug mode with nodeExecutions)
	const hasExecutionData = stableNodeExecutions.length > 0;

	// Handler for node click - select the node (no longer auto-scopes assistant)
	// Supports Shift+click for multi-selection
	const handleNodeClick: NodeMouseHandler = useCallback(
		(event, node) => {
			// Close any open context menus when clicking on a node
			closeAllContextMenus();
			// Collapse any expanded sub-workflow preview
			setExpandedSubWorkflow(null);

			const clickedNodeType = node.id.split('-')[0] ?? 'unknown';
			telemetryService.trackEvent('workflow.node.clicked', { nodeType: clickedNodeType });

			// In monitoring mode, just call the callback
			if (isMonitoringMode) {
				onMonitoringNodeClick?.(node.id);
				return;
			}

			// Extract connectionName from node data for connector color lookup
			const nodeData = node.data as { connectionName?: string };
			const connectionName = nodeData?.connectionName;

			// Check if Shift key is held for multi-selection toggle
			const isShiftKey = event.shiftKey;

			// Determine the node type and name from the node ID
			if (node.id.startsWith("trigger-")) {
				const name = node.id.replace("trigger-", "");
				if (isShiftKey) {
					// Toggle selection (add if not selected, remove if selected)
					toggleNodeSelection({ id: node.id, name, type: "trigger", ...(connectionName && { connectionName }) });
				} else {
					// Open editor without changing multi-selection
					openNodeEditor({ id: node.id, name, type: "trigger", ...(connectionName && { connectionName }) });
				}
			} else if (node.id.startsWith("tool-")) {
				// Get tool info from node data
				const toolData = node.data as {
					parentAgentId?: string;
					parentAgentName?: string;
					toolName?: string;
					connectionName?: string;
				};
				if (toolData.parentAgentId && toolData.toolName) {
					const agentName = toolData.parentAgentName || toolData.parentAgentId.replace("action-", "");
					if (isShiftKey) {
						// Toggle selection (add if not selected, remove if selected)
						toggleNodeSelection({ 
							id: node.id, 
							name: toolData.toolName, 
							type: "tool", 
							parentAgentName: agentName,
							...(toolData.connectionName && { connectionName: toolData.connectionName }) 
						});
					} else {
						selectTool(agentName, toolData.toolName);
					}
				}
			} else if (node.id.startsWith("action-")) {
				const graphData = node.data as { actionName?: string; scopeParentId?: string; branchType?: string; connectionName?: string };
				const name = graphData.actionName ?? node.id.replace("action-", "");
				// Detect scope context from graph node data (set during graph conversion)
				const scopeContext = buildScopeContext(graphData.scopeParentId, graphData.branchType);
				if (isShiftKey) {
					// Toggle selection (add if not selected, remove if selected)
					toggleNodeSelection({ id: node.id, name, type: "action", ...(connectionName && { connectionName }), ...(scopeContext && { scopeContext }) });
				} else {
					// Open editor without changing multi-selection
					openNodeEditor({ id: node.id, name, type: "action", ...(connectionName && { connectionName }), ...(scopeContext && { scopeContext }) });
				}
			}
		},
		[closeAllContextMenus, isMonitoringMode, onMonitoringNodeClick, openNodeEditor, selectTool, toggleNodeSelection],
	);

	// Handler for box selection end (fires when user releases mouse after drawing selection box)
	const handleSelectionEnd = useCallback(() => {
		// In monitoring mode, ignore selection changes
		if (isMonitoringMode) return;

		// Get currently selected nodes from ReactFlow state
		const selectedFlowNodes = nodes.filter((n) => n.selected);

		// Filter to only actionable nodes (triggers, actions, tools) and map to SelectedNode format
		const selectedNodesList = selectedFlowNodes
			.filter((n) => n.id.startsWith("trigger-") || n.id.startsWith("action-") || n.id.startsWith("tool-"))
			.map((n) => {
				const nodeData = n.data as { connectionName?: string; agentName?: string; toolName?: string };
				const connectionName = nodeData?.connectionName;
				
				if (n.id.startsWith("trigger-")) {
					const name = n.id.replace("trigger-", "");
					return { 
						id: n.id, 
						name, 
						type: "trigger" as const, 
						...(connectionName && { connectionName }) 
					};
				} else if (n.id.startsWith("tool-")) {
					// Tool nodes have id format: tool-{agentName}-{toolName}
					const parentAgentName = nodeData?.agentName ?? "";
					const toolName = nodeData?.toolName ?? n.id.replace(/^tool-[^-]+-/, "");
					return { 
						id: n.id, 
						name: toolName, 
						type: "tool" as const,
						parentAgentName,
						toolName,
					};
				} else {
					const name = n.id.replace("action-", "");
					return { 
						id: n.id, 
						name, 
						type: "action" as const, 
						...(connectionName && { connectionName }) 
					};
				}
			});

		// If shift is held, ADD to selection instead of replacing
		if (isShiftKeyDown.current) {
			addNodesToSelection(selectedNodesList);
		} else {
			selectNodes(selectedNodesList);
		}
	}, [isMonitoringMode, selectNodes, addNodesToSelection, nodes]);

	// Handler for edge add button click (between two nodes)
	const handleEdgeAddClick = useCallback(
		(sourceId: string, targetId: string) => {
			closeAllContextMenus();
			setPanelState({
				isOpen: true,
				mode: "action",
				insertionPoint: { sourceNodeId: sourceId, targetNodeId: targetId },
			});
		},
		[closeAllContextMenus],
	);

	// Handler for the edge's "Add Agent" button (dual-button entry point).
	// Opens a side panel with cards for picking the agent variant.
	const handleEdgeAddAgent = useCallback(
		(sourceId: string, targetId: string) => {
			closeAllContextMenus();
			setPanelState(prev => ({ ...prev, isOpen: false }));
			setAuxPickerView({
				kind: 'agent-type',
				insertionPoint: { sourceNodeId: sourceId, targetNodeId: targetId },
			});
		},
		[closeAllContextMenus],
	);

	// Insert a new agent action at the insertion point captured by the edge
	// dual-button. Mirrors the linear-insertion branch of `handleOperationSelect`
	// without scope/parallel-branch handling (addButton edges only render
	// between non-scoped, non-parallel nodes).
	const handleAgentTypeSelectFromEdge = useCallback(
		(selection: AgentSelection) => {
			const insertionPoint =
				auxPickerView?.kind === 'agent-type' ? auxPickerView.insertionPoint : null;
			setAuxPickerView(null);
			if (!insertionPoint) return;

			const agentOp = AGENT_OPERATIONS[0];
			if (!agentOp) return;
			const operation = buildAgentOperationFromSelection(agentOp, selection);

			const isHorizontalLayout = layoutOrientation === 'horizontal';
			const sourceNode = graphNodes.find((n) => n.id === insertionPoint.sourceNodeId);
			const targetNode = insertionPoint.targetNodeId
				? graphNodes.find((n) => n.id === insertionPoint.targetNodeId)
				: undefined;
			const isTriggerSource = insertionPoint.sourceNodeId.startsWith('trigger-');
			const sourcePosition = sourceNode?.position ?? DEFAULT_TRIGGER_POSITION;
			const targetPosition = targetNode?.position;
			const newPosition = calculateInsertionPosition(
				sourcePosition,
				targetPosition,
				isHorizontalLayout,
				undefined,
				sourceNode?.measured,
				isTriggerSource ? DEFAULT_NODE_WIDTH : 0,
				0,
			);

			const actionName = generateUniqueActionName(operation.name.replace(/\s+/g, '_'));
			const action: Action = createActionFromOperation(
				operation,
				newPosition,
				validApiConnections,
				connectionReferences,
			);
			addAction(actionName, action, insertionPoint);
			openNodeEditor({ id: `action-${actionName}`, name: actionName, type: 'action' });
		},
		[
			auxPickerView,
			graphNodes,
			layoutOrientation,
			validApiConnections,
			connectionReferences,
			generateUniqueActionName,
			addAction,
			openNodeEditor,
		],
	);


	// Handler for adding action at the end of workflow (after terminal node)
	const handleTerminalAddClick = useCallback(
		(sourceNodeId: string) => {
			closeAllContextMenus();
			setPanelState({
				isOpen: true,
				mode: "action",
				insertionPoint: { sourceNodeId },
			});
		},
		[closeAllContextMenus]
	);

	// Hover-revealed "Add agent" affordance on the plain end-of-workflow terminal.
	// Routes through the same agent-type picker the in-line edge button uses, but
	// with no targetNodeId since the insertion is an append.
	const handleTerminalAddAgent = useCallback(
		(sourceNodeId: string) => {
			closeAllContextMenus();
			setPanelState(prev => ({ ...prev, isOpen: false }));
			setAuxPickerView({
				kind: 'agent-type',
				insertionPoint: { sourceNodeId },
			});
		},
		[closeAllContextMenus],
	);

	// Handler for adding a parallel branch after a node.
	// Uses insertionPoint with sourceNodeId only (no targetNodeId) — the store's
	// addAction creates runAfter: { source: ['Succeeded'] } which is parallel to
	// any existing actions that also run after the same source.
	const handleAddParallelBranch = useCallback(
		(sourceNodeId: string) => {
			closeAllContextMenus();
			setPanelState({
				isOpen: true,
				mode: "action",
				insertionPoint: { sourceNodeId },
			});
		},
		[closeAllContextMenus]
	);

	// Handler for adding action on a True/False/loop branch edge of an
	// If/ForEach/Until when the branch already has children. Re-parents the
	// existing target so the new action becomes the branch root and the prior
	// root runs after it.
	const handleBranchEdgeAddClick = useCallback(
		(
			sourceNodeId: string,
			targetNodeId: string,
			branchType: 'true' | 'false' | 'loop',
			parentActionName: string,
		) => {
			closeAllContextMenus();
			setPanelState({
				isOpen: true,
				mode: "action",
				insertionPoint: {
					sourceNodeId,
					targetNodeId,
					scope: { parentActionName, branchType },
				},
			});
		},
		[closeAllContextMenus],
	);

	// Handler for adding action inside a control flow scope (e.g., Foreach loop body)
	const handleScopedAddClick = useCallback(
		(sourceNodeId: string, branchType: string) => {
			// Extract the simple action name (last segment of scoped path)
			const scopedPath = sourceNodeId.replace(/^(trigger-|action-)/, '');
			const actionName = scopedPath.includes('/') ? scopedPath.split('/').pop()! : scopedPath;

			// Block adding more actions inside a ForEach that already has one
			if (branchType === 'loop' && isForEachFull(workflowActions, actionName)) {
				return;
			}

			closeAllContextMenus();
			setPanelState({
				isOpen: true,
				mode: "action",
				insertionPoint: {
					sourceNodeId,
					scope: { parentActionName: actionName, branchType },
				},
			});
		},
		[closeAllContextMenus, workflowActions]
	);

	// Handler for clicking on placeholder trigger node to add a trigger
	const handlePlaceholderTriggerClick = useCallback(() => {
		closeAllContextMenus();
		// Find the placeholder node to get its position so the trigger is added there
		const placeholderNode = graphNodesRef.current.find(n => n.id === 'placeholder-trigger');
		if (placeholderNode?.position) {
			setClickPosition(placeholderNode.position);
		}
		setPanelState({
			isOpen: true,
			mode: "trigger",
			insertionPoint: null,
		});
	}, [closeAllContextMenus]);

// NOTE: edgesWithHandlers was moved inline into the sync effect to avoid stale closure issues
	// The edge computation now uses graphEdgesRef.current directly

	// Find unconnected output handles for all nodes.
	// For control flow nodes (if, switch, foreach), each branch handle is checked independently.
	// Foreach "loop" handle is excluded — loop body insertion uses a hover "+" on the node itself.
	type TerminalHandle = { nodeId: string; handle: string; nodeType: string | undefined; actionName: string; scopeParentId?: string | undefined; branchType?: string | undefined };
	const terminalHandles = useMemo((): TerminalHandle[] => {
		// Build set of connected (source::sourceHandle) pairs
		const connectedHandles = new Set<string>();
		graphEdges.forEach((edge) => {
			const handle = edge.sourceHandle ?? 'output';
			connectedHandles.add(`${edge.source}::${handle}`);
		});

		const multiHandleTypes = new Set(['if', 'switch', 'foreach']);
		const result: TerminalHandle[] = [];

		graphNodes.forEach((node) => {
			const nodeType = node.type ?? '';
			// Scope containers are visual-only wrappers with no Handle components — skip them.
			if (nodeType === 'scopeContainer') return;
			const actionName = node.data?.actionName ?? node.id.replace(/^(trigger-|action-)/, '');
			const scopeParentId = node.data?.scopeParentId;
			const branchType = node.data?.branchType;

			// Skip terminal handles for actions inside a ForEach that already has an action
			if (scopeParentId && branchType === 'loop') {
				const scopeParentActionName = scopeParentId.replace(/^action-/, '');
				if (isForEachFull(workflowActions, scopeParentActionName)) {
					return;
				}
			}

			if (multiHandleTypes.has(nodeType)) {
				// Control flow: check each handle independently
				const cases = node.data?.cases as string[] | undefined;
				const handles = getNodeOutputHandles(nodeType, cases);
				handles.forEach((handle) => {
					// Note: foreach "loop" and if "true"/"false" handles DO get terminal
					// edges so they render branch-colored dashed stub-edges ending in a
					// + button (matching the design language of all other empty handles).
					if (!connectedHandles.has(`${node.id}::${handle}`)) {
						result.push({ nodeId: node.id, handle, nodeType, actionName, scopeParentId, branchType });
					}
				});
			} else {
				// Regular node: terminal if no outgoing edges at all
				const hasOutgoing = graphEdges.some((e) => e.source === node.id);
				if (!hasOutgoing) {
					result.push({ nodeId: node.id, handle: 'output', nodeType, actionName, scopeParentId, branchType });
				}
			}
		});

		return result;
	}, [graphNodes, graphEdges, workflowActions]);

	// Derive terminalNodeIds from terminalHandles for phantom node generation
	const terminalNodeIds = useMemo(
		() => new Set(terminalHandles.map((th) => th.nodeId)),
		[terminalHandles]
	);

	// Ref so drag-and-drop drop handler can resolve scope context from a phantom hit-test
	const terminalHandlesRef = useRef(terminalHandles);
	terminalHandlesRef.current = terminalHandles;

	// Remeasure source nodes whenever the set of terminal (unconnected) handles
	// changes — without this, the freshly-mounted terminal phantoms render
	// against stale handle bounds and the connecting edges disappear.
	const prevTerminalHandlesKeyRef = useRef<string>('');
	useEffect(() => {
		if (isReadOnlyCanvas) return;
		const key = terminalHandles.map((th) => `${th.nodeId}::${th.handle}`).sort().join('|');
		if (key === prevTerminalHandlesKeyRef.current) return;
		prevTerminalHandlesKeyRef.current = key;
		return scheduleHandleRemeasure(
			terminalHandles.map((th) => th.nodeId),
			updateNodeInternals,
		);
	}, [terminalHandles, isReadOnlyCanvas, updateNodeInternals]);

	// Remeasure both endpoints of every edge whenever the workflow's edge set
	// is rebuilt (node delete, draft↔published swap, code-editor edit). Without
	// this, surviving edges silently disappear until the user pans, zooms, or
	// refreshes. Keyed on graphEdges identity (useWorkflowGraph returns a new
	// array per workflow change) so UI-only re-renders don't thrash.
	const prevGraphEdgesIdentityRef = useRef(graphEdges);
	useEffect(() => {
		if (prevGraphEdgesIdentityRef.current === graphEdges) return;
		prevGraphEdgesIdentityRef.current = graphEdges;
		const endpointIds: string[] = [];
		for (const edge of graphEdges) endpointIds.push(edge.source, edge.target);
		return scheduleHandleRemeasure(endpointIds, updateNodeInternals);
	}, [graphEdges, updateNodeInternals]);

	// Create terminal edges for each unconnected handle.
	// Control flow nodes can have multiple terminal edges (e.g., Foreach loop + done).
	// Skip in monitoring mode - terminal edges are for adding nodes (design mode only)
	const terminalEdges = useMemo((): Edge<TerminalEdgeData>[] => {
		if (isReadOnlyCanvas) {
			return [];
		}

		// Determine if a handle represents a nested scope (loop body, if branch, switch case)
		const isNestedScope = (nodeType: string | undefined, handle: string): boolean => {
			if (nodeType === 'foreach' && handle === 'loop') return true;
			if (nodeType === 'if' && (handle === 'true' || handle === 'false')) return true;
			if (nodeType === 'switch' && (handle.startsWith('case-') || handle === 'default')) return true;
			return false;
		};

		return terminalHandles.map((th) => {
			const useScope = isNestedScope(th.nodeType, th.handle);
			// If this terminal node is a child of a ForEach/scope, use scoped add
			// so the new action is added inside the loop body, not at the top level.
			const scopeParentActionName = th.scopeParentId?.replace(/^action-/, '');
			const isInsideScope = !!(th.scopeParentId && th.branchType === 'loop' && scopeParentActionName);
			// The hover-revealed "Add agent" affordance is only offered on the
			// plain end-of-workflow terminal (no branch stub, no scoped loop) to
			// keep colored branch stubs and inside-foreach insertion focused.
			const isPlainTerminal = !useScope && !isInsideScope;

			const getAddClickHandler = (): (sourceId: string) => void => {
				if (useScope) {
					return (sourceId) => handleScopedAddClick(sourceId, th.handle);
				}
				if (isInsideScope) {
					return (sourceId) => {
						closeAllContextMenus();
						setPanelState({
							isOpen: true,
							mode: "action",
							insertionPoint: {
								sourceNodeId: sourceId,
								scope: { parentActionName: scopeParentActionName!, branchType: 'loop' },
							},
						});
					};
				}
				return handleTerminalAddClick;
			};

			return {
				id: `terminal-${th.nodeId}-${th.handle}`,
				source: th.nodeId,
				sourceHandle: th.handle,
				target: `terminal-target-${th.nodeId}-${th.handle}`,
				type: "terminal",
				data: {
					onAddClick: getAddClickHandler(),
					...(isPlainTerminal ? { onAddAgentClick: handleTerminalAddAgent } : {}),
					scopeLabel: isInsideScope ? `inside ${scopeParentActionName} loop` : undefined,
				},
			};
		});
	}, [terminalHandles, handleTerminalAddClick, handleTerminalAddAgent, handleScopedAddClick, closeAllContextMenus, isReadOnlyCanvas]);

	// Generate tool nodes and edges for expanded agents (fan layout below agent)
	const { toolNodes, toolEdges } = useMemo(() => {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const toolNodes: Node<any>[] = [];
		const toolEdges: Edge[] = [];

		// Find agent nodes and generate tool nodes
		graphNodes.forEach((node) => {
			if (node.type !== "agent") return;

			const agentName = node.data.actionName;
			const isExpanded = expandedAgents.has(agentName);

			const tools = node.data.tools;
			if (!tools) return;

			// In monitoring/debug mode (when we have execution data):
			// - Collapsed (default): show ONLY invoked tools for current iteration
			// - Expanded: show ALL tools, mark non-invoked as skipped
			// - If no iteration data yet: show no tools until data loads (unless expanded)
			// In designer mode (no execution data): show all tools when expanded, hide when collapsed
			let toolEntries = Object.entries(tools);
			let invokedToolNames: Set<string> | null = null;

			// Use hasExecutionData instead of isMonitoringMode to support debug mode
			const hasIterationDataForMode = hasExecutionData && agentIterationsData?.has(agentName);

			if (hasIterationDataForMode) {
				const agentIterations = agentIterationsData?.get(agentName);
				const currentIterationIndex = stableAgentIterations[agentName] ?? 0;
				const currentIteration = agentIterations?.[currentIterationIndex];

				if (currentIteration) {
					const invoked = new Set(currentIteration.tools.map(t => t.toolName));
					invokedToolNames = invoked;

					if (!isExpanded) {
						// Collapsed: show only invoked tools
						toolEntries = toolEntries.filter(([toolName]) => invoked.has(toolName));
					}
					// Expanded: show all tools, we'll mark non-invoked as skipped below
				} else if (!isExpanded) {
					// No iteration data yet and not expanded: hide all tools
					return;
				}
				// If expanded but no data: show all tools (will be marked appropriately when data loads)
			} else if (!isExpanded) {
				// Designer mode (no execution data): hide all tools when collapsed
				return;
			}

			const toolCount = toolEntries.length;
			if (toolCount === 0) return;

			// Layout configuration (positions are relative to agent node, not absolute)
			// n8n-style: tools flow ABOVE agent, model flows BELOW
			const agentNodeWidth = 60; // Agent hexagon width
			const toolNodeHeight = 80; // Approximate tool node height including label
			const toolNodeWidth = 120; // Approximate tool node width
			const baseVerticalGap = 80; // Gap between agent top and tool bottom (longer edges)
			const staggerOffset = 30; // Vertical offset for alternating rows (goes UP)
			const horizontalGap = 20; // Gap between tools

			// Calculate total width needed for all tools
			const totalToolsWidth =
				toolCount * toolNodeWidth + (toolCount - 1) * horizontalGap;

			// Calculate tool positions RELATIVE to agent (for parentId grouping)
			// Tools go ABOVE the agent (negative Y values)
			const relativeStartX = agentNodeWidth / 2 - totalToolsWidth / 2;
			const relativeBaseY = -(baseVerticalGap + toolNodeHeight); // ABOVE agent

			toolEntries.forEach(([toolName, tool], index) => {
				// Default fan-layout position (relative to the parent agent).
				// Stagger odd indices upward so labels don't collide.
				const defaultX = relativeStartX + index * (toolNodeWidth + horizontalGap);
				const defaultY = relativeBaseY - (index % 2 === 1 ? staggerOffset : 0);

				// Honor any user-dragged position stored on the agent.
				const storedPosition = node.data.toolNodePositions?.[toolName];
				const toolX = storedPosition?.x ?? defaultX;
				const toolY = storedPosition?.y ?? defaultY;
				// Extract action info from tool for connector icon
				// Tool has actions property with the inner action(s)
				const toolActions = tool.actions ?? {};
				const actionEntries = Object.entries(toolActions);
				const firstActionEntry = actionEntries[0];
				const innerActionName = firstActionEntry?.[0]; // The action name (key)

				// Tool node ID includes inner action name for selection matching with execution log
				const toolNodeId = `tool-${agentName}-${innerActionName ?? toolName}`;
				const firstAction = firstActionEntry?.[1]; // The action definition
				const innerActionType = firstAction?.type ?? 'ApiConnection';

				// Resolve connector identity by dispatching on the action's `type`:
				//   - 'ServiceProvider' → inputs.serviceProviderConfiguration.{ operationId, connectionName }
				//     (connectorId derived from the operationId path: /serviceProviders/{name}/...)
				//   - ApiConnection family → inputs.host.{ apiId, operationId, connection.referenceName }
				let connectorId: string | undefined;
				let operationId: string | undefined;
				let connectionName: string | undefined;
				if (innerActionType === 'ServiceProvider') {
					const spConfig = firstAction?.inputs?.serviceProviderConfiguration as
						| { operationId?: string; connectionName?: string }
						| undefined;
					connectorId = getServiceProviderConnectorId(firstAction) ?? undefined;
					operationId = spConfig?.operationId;
					connectionName = spConfig?.connectionName;
				} else {
					const actionHost = firstAction?.inputs?.host as {
						apiId?: string;
						operationId?: string;
						connection?: { referenceName?: string };
					} | undefined;
					connectorId = getActionHostApiId(firstAction) ?? undefined;
					operationId = actionHost?.operationId;
					connectionName = actionHost?.connection?.referenceName
						?? getActionReferenceName(firstAction)
						?? undefined;
				}
				// Note: connector display info (icon / brand color / name) is no longer
				// persisted on action metadata. The tool node resolves it at render time
				// from the connector manifest by inner-action name.

				// Determine if this tool was invoked in the current iteration
				// Used for monitoring mode to show non-invoked tools as skipped
				const wasInvokedInIteration = invokedToolNames ? invokedToolNames.has(toolName) : true;

				// Create tool node with staggered Y position
				// actionName is the inner action's name (e.g., "Send_an_email")
				// label is the user-facing tool name
				toolNodes.push({
					id: toolNodeId,
					type: "tool",
					position: { x: toolX, y: toolY },
					// parentId makes tool move with agent during drag (ReactFlow built-in grouping)
					parentId: node.id,
					data: {
						label: toolName,
						toolName,
						description: tool.description,
						parentAgentId: node.id,
						parentAgentName: agentName,
						actionType: innerActionType,
						// AgentTool.type discriminator — used by canvas to apply MCP-specific styling.
						toolKind: tool.type,
						// actionName shows the inner action name in tooltip subtitle
						actionName: innerActionName,
						// Connector info for icon
						connectorId,
						operationId,
						connectionName,
						// Mark if tool was not invoked (for monitoring/debug mode skipped state)
						wasNotInvoked: hasExecutionData && !wasInvokedInIteration,
						// Sub-workflow expand/collapse state (only for Workflow-type tools)
						...(innerActionType === 'Workflow' && {
							toolNodeId,
							isSubWorkflowExpanded: expandedSubWorkflow === toolNodeId,
							onSubWorkflowExpandToggle: handleSubWorkflowExpandToggle,
						}),
					},
					selectable: true,
				});

				// Create dotted edge from agent to tool (tinted to match agent identity)
				const isFoundryAgent =
					node.data.agentModelType === 'FoundryAgentService' ||
					node.data.agentModelType === 'FoundryAgentServiceV2';
				toolEdges.push({
					id: `${node.id}->${toolNodeId}`,
					source: node.id,
					target: toolNodeId,
					type: "dotted",
					sourceHandle: "tools",
					targetHandle: "input",
					data: { agentVariant: isFoundryAgent ? 'foundry' : 'native' },
				});
			});
		});

		return { toolNodes, toolEdges };
	}, [graphNodes, expandedAgents, hasExecutionData, agentIterationsData, stableAgentIterations, expandedSubWorkflow, handleSubWorkflowExpandToggle]);

	// Generate model nodes for agents (positioned BELOW agent - n8n cluster style)
	const { modelNodes, modelEdges } = useMemo(() => {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const modelNodes: Node<any>[] = [];
		const modelEdges: Edge[] = [];

		// Find agent nodes and generate model nodes
		graphNodes.forEach((node) => {
			if (node.type !== "agent") return;

			const agentData = node.data as FloatingAgentNodeData;
			const agentName = agentData.actionName;

			// Get model deployment info from agent
			const modelDeployment = agentData.modelDeployment;
			const connectionName = agentData.connectionName;
			// FoundryHostedAgent uses a hosted agent connection instead of a model deployment
			const isHostedAgent = agentData.agentModelType === 'FoundryHostedAgent';
			const isConfigured = isHostedAgent ? !!connectionName : !!modelDeployment;

			// Model node positioning (BELOW agent)
			const agentNodeWidth = 60;
			const modelNodeWidth = 100;
			const verticalGap = 60;

			// Center model below agent
			const modelX = (agentNodeWidth - modelNodeWidth) / 2;
			const modelY = 70 + verticalGap; // Below agent icon + label

			const modelNodeId = `model-${agentName}`;

			// Create model node
			modelNodes.push({
				id: modelNodeId,
				type: "model",
				position: { x: modelX, y: modelY },
				parentId: node.id,
				data: {
					label: isConfigured
						? (isHostedAgent
							? (connectionName ?? 'Hosted Agent')
							: (modelDeployment?.split('/').pop() ?? 'Model'))
						: 'Configure',
					modelDeployment: modelDeployment ?? '',
					parentAgentName: agentName,
					connectionName,
					isConfigured,
				},
				draggable: false,
				selectable: true,
			});

			// Create edge from agent to model (tinted to match agent identity)
			const isFoundryAgent =
				agentData.agentModelType === 'FoundryAgentService' ||
				agentData.agentModelType === 'FoundryAgentServiceV2';
			modelEdges.push({
				id: `${node.id}->model`,
				source: node.id,
				target: modelNodeId,
				type: "dotted",
				sourceHandle: "model",
				targetHandle: "input",
				data: { agentVariant: isFoundryAgent ? 'foundry' : 'native' },
			});
		});

		return { modelNodes, modelEdges };
	}, [graphNodes]);

	// Handlers for node toolbar actions (passed to node data)
	const handleNodeDelete = useCallback((nodeType: 'trigger' | 'action', nodeName: string) => {
		if (nodeType === 'trigger') {
			deleteTrigger(nodeName);
		} else {
			deleteAction(nodeName);
		}
	}, [deleteAction, deleteTrigger]);

	const handleNodeEditWithAI = useCallback((_nodeType: 'trigger' | 'action', nodeName: string) => {
		setEditScope({ type: 'node', name: nodeName });
		// Open the chat sidebar if it's not already open
		if (!isChatSidebarOpen && onToggleChat) {
			onToggleChat();
		}
	}, [setEditScope, isChatSidebarOpen, onToggleChat]);

	const handleNodeDuplicate = useCallback((nodeType: 'trigger' | 'action', nodeName: string) => {
		if (nodeType === 'action') {
			// Look up scope context from the original node before duplicating
			const originalGraphNode = graphNodes.find((n) => n.id === `action-${nodeName}`);
			const scopeContext = buildScopeContext(originalGraphNode?.data?.scopeParentId, originalGraphNode?.data?.branchType);
			const newName = duplicateAction(nodeName);
			if (newName) {
				// Select the newly duplicated node and open editor
				openNodeEditor({ id: `action-${newName}`, name: newName, type: 'action', ...(scopeContext && { scopeContext }) });
			}
		}
		// Note: Trigger duplication not supported (workflows typically have one trigger)
	}, [duplicateAction, openNodeEditor, graphNodes]);

	const handleNodeOpen = useCallback((nodeType: 'trigger' | 'action', nodeName: string) => {
		const id = nodeType === 'trigger' ? `trigger-${nodeName}` : `action-${nodeName}`;
		const graphNode = graphNodes.find((n) => n.id === id);
		const scopeContext = nodeType === 'action'
			? buildScopeContext(graphNode?.data?.scopeParentId, graphNode?.data?.branchType)
			: undefined;
		openNodeEditor({ id, name: nodeName, type: nodeType, ...(scopeContext && { scopeContext }) });
	}, [openNodeEditor, graphNodes]);

	// Keyboard navigation callbacks - adapt from node ID to store functions.
	// Tab / arrow keys only FOCUS a node (highlight it); the editor only opens
	// when the user explicitly presses Enter / Space (wired separately via
	// onNodeEdit → openNodeEditor).
	const handleKeyboardNodeSelect = useCallback((nodeId: string) => {
		if (nodeId.startsWith('trigger-')) {
			const name = nodeId.replace('trigger-', '');
			focusNode({ id: nodeId, name, type: 'trigger' });
		} else if (nodeId.startsWith('action-')) {
			const name = nodeId.replace('action-', '');
			const graphNode = graphNodes.find((n) => n.id === nodeId);
			const scopeContext = buildScopeContext(graphNode?.data?.scopeParentId, graphNode?.data?.branchType);
			focusNode({ id: nodeId, name, type: 'action', ...(scopeContext && { scopeContext }) });
		}
	}, [focusNode, graphNodes]);

	const handleKeyboardNodeDelete = useCallback((nodeId: string) => {
		if (nodeId.startsWith('trigger-')) {
			const name = nodeId.replace('trigger-', '');
			deleteTrigger(name);
		} else if (nodeId.startsWith('action-')) {
			const name = nodeId.replace('action-', '');
			deleteAction(name);
		}
	}, [deleteAction, deleteTrigger]);

	const handleKeyboardNodeEdit = useCallback((nodeId: string) => {
		if (nodeId.startsWith('trigger-')) {
			const name = nodeId.replace('trigger-', '');
			openNodeEditor({ id: nodeId, name, type: 'trigger' });
		} else if (nodeId.startsWith('action-')) {
			const name = nodeId.replace('action-', '');
			const graphNode = graphNodes.find((n) => n.id === nodeId);
			const scopeContext = buildScopeContext(graphNode?.data?.scopeParentId, graphNode?.data?.branchType);
			openNodeEditor({ id: nodeId, name, type: 'action', ...(scopeContext && { scopeContext }) });
		}
	}, [openNodeEditor, graphNodes]);

	const handleKeyboardSelectAll = useCallback(() => {
		// Select all workflow nodes (triggers and actions, excluding phantom/tool nodes)
		const allNodes = nodes
			.filter(n => n.id.startsWith('trigger-') || n.id.startsWith('action-'))
			.map(n => {
				const type: 'trigger' | 'action' = n.id.startsWith('trigger-') ? 'trigger' : 'action';
				const name = n.id.replace(/^(trigger-|action-)/, '');
				return { id: n.id, name, type };
			});
		selectNodes(allNodes);
	}, [nodes, selectNodes]);

	const handleKeyboardDuplicateSelected = useCallback(() => {
		// Duplicate the first selected action (trigger duplication not supported)
		const selectedAction = selectedNodes.find(n => n.type === 'action');
		if (selectedAction) {
			const originalGraphNode = graphNodes.find((n) => n.id === selectedAction.id);
			const scopeContext = buildScopeContext(originalGraphNode?.data?.scopeParentId, originalGraphNode?.data?.branchType);
			const newName = duplicateAction(selectedAction.name);
			if (newName) {
				openNodeEditor({ id: `action-${newName}`, name: newName, type: 'action', ...(scopeContext && { scopeContext }) });
			}
		}
	}, [selectedNodes, duplicateAction, openNodeEditor, graphNodes]);

	// Keyboard navigation for canvas - enables arrow keys, Tab, Enter, Delete, etc.
	useCanvasKeyboardNavigation({
		nodes,
		edges,
		selectedNodeIds: selectedNodes.map(n => n.id),
		onNodeSelect: handleKeyboardNodeSelect,
		onNodeDelete: handleKeyboardNodeDelete,
		onNodeEdit: handleKeyboardNodeEdit,
		onDeselectAll: clearSelection,
		onSelectAll: handleKeyboardSelectAll,
		onDuplicateSelected: handleKeyboardDuplicateSelected,
		enabled: !isReadOnlyCanvas, // Disable in monitoring and published mode
		layoutOrientation,
	});

	// Sync nodes and edges when workflow changes
	useEffect(() => {
		// Detect if graphNodes changed identity (workflow data change vs. UI-only change)
		const graphDataChanged = graphNodes !== prevGraphNodesRef.current;
		prevGraphNodesRef.current = graphNodes;

		// Don't sync while dragging - positions will be updated when drag ends
		if (isDraggingRef.current) {
			return;
		}

		// Don't sync during post-drag settling period - wait for state to stabilize
		// BUT always sync when workflow data changed (e.g., code editor edits)
		if (postDragSettlingRef.current && !graphDataChanged) {
			postDragSettlingRef.current = false;
			return;
		}
		postDragSettlingRef.current = false;

		// Defensive check: Don't sync to empty nodes/edges if we have a workflow with content
		// This prevents the canvas from going blank during timing windows where
		// graphNodes/graphEdges temporarily becomes empty due to state update race conditions.
		// We only allow empty sync when the workflow itself is legitimately empty.
		const triggerCount = Object.keys(workflow?.definition?.triggers ?? {}).length;
		const actionCount = Object.keys(workflow?.definition?.actions ?? {}).length;
		const noteCount = Object.keys(workflow?.notes ?? {}).length;
		const workflowHasContent = triggerCount > 0 || actionCount > 0;
		const workflowHasNotes = noteCount > 0;

		if (graphNodes.length === 0 && workflowHasContent) {
			// Workflow has content but graphNodes is empty - skip this sync
			// A subsequent effect run will have the correct graphNodes
			return;
		}

		// Safety: If we previously had workflow nodes and now graphNodes is empty,
		// but we still have notes, something is wrong - skip the sync
		if (graphNodes.length === 0 && workflowHasNotes && nodesRef.current.some(n => n.id.startsWith('trigger-') || n.id.startsWith('action-'))) {
			return;
		}

		// Also check edges: skip sync only if the workflow data implies edges but
		// graphEdges is empty — that's a transient race. A workflow where every
		// top-level action is detached and no runAfter exists legitimately has
		// zero edges (e.g., user just deleted the last edge — issue #955 followup).
		const expectedEdgeCount = countExpectedEdges(workflow);
		if (graphEdges.length === 0 && expectedEdgeCount > 0) {
			// Workflow should have edges but graphEdges is empty - skip this sync
			// A subsequent effect run will have the correct graphEdges
			return;
		}

		const isHorizontal = layoutOrientation === "horizontal";

		// Create deep copies to ensure React detects the changes
		// Mark nodes as selected based on the store's selectedNode or monitoring selection
		// Inject expansion state and toggle handler into agent nodes
		// Merge execution data in monitoring mode or debug mode
		const processedGraphNodes = graphNodes.map((node) => {
			// Determine selection state - use monitoring selection if in monitoring mode.
			// Fall back to actionName match for scoped branch nodes (e.g., canvas
			// "action-IsApproved/Compose_1" vs log selection "action-Compose_1").
			const isSelected = isMonitoringMode
				? (node.id === monitoringSelectedNodeId
					|| monitoringSelectedNodeId === `action-${node.data.actionName}`)
				: selectedNode?.id === node.id;

			// Get execution data for this node if we have execution data (monitoring or debug mode).
			// Try the node's full ID first (works for top-level nodes).
			// Fall back to "action-{actionName}" for nodes inside If/Switch/Foreach branches whose
			// canvas IDs are scoped (e.g., "action-IsApproved/Compose_1") but whose execution data
			// is indexed by the plain action name (e.g., "action-Compose_1").
			const execution = hasExecutionData
				? (executionLookup.get(node.id) ?? executionLookup.get(`action-${node.data.actionName}`))
				: undefined;

			// Check if this node is dirty (changed since execution was captured)
			const isDirty = dirtyNodeIds.has(node.id);

			// Determine node type for toolbar callbacks
			const nodeType: 'trigger' | 'action' = node.id.startsWith('trigger-') ? 'trigger' : 'action';
			const nodeName = node.id.replace(/^(trigger-|action-)/, '');

			const baseNode = {
				...node,
				data: {
					...node.data,
					// Merge execution data, or mark as skipped in monitoring mode only
					// (not debug mode, where unexecuted nodes should remain editable)
					...(execution
						? { executionStatus: execution.status, executionDuration: execution.duration }
						: isMonitoringMode && hasExecutionData && { executionStatus: 'skipped' as const }
					),
					// Mark dirty nodes in debug mode
					...(isExecutionMode && isDirty && { isDirty: true }),
					// Toolbar action handlers (only in design mode)
					...(!isReadOnlyCanvas && {
						onOpen: () => handleNodeOpen(nodeType, nodeName),
						onDelete: () => handleNodeDelete(nodeType, nodeName),
						onEditWithAI: () => handleNodeEditWithAI(nodeType, nodeName),
						// Only actions can be duplicated (not triggers)
						...(nodeType === 'action' && {
							onDuplicate: () => handleNodeDuplicate(nodeType, nodeName),
						}),
					}),
				},
				selected: isSelected,
				// Disable dragging in monitoring mode or when the user has locked the canvas
				draggable: !isReadOnlyCanvas && !isCanvasLocked,
			};

			// Inject agent-specific data
			if (node.type === "agent") {
				const agentName = node.data.actionName;
				// Get iteration data for this agent
				// Use undefined for iterationCount when no execution exists to distinguish
				// "not executed" from "executed zero times"
				const currentIteration = stableAgentIterations[agentName] ?? 0;
				const iterationCount = execution ? execution.iterationCount : undefined;
				const isToolMenuOpen =
					toolTypeMenuState.open &&
					toolTypeMenuState.targetAgentName === agentName;

				return {
					...baseNode,
					data: {
						...baseNode.data,
						isToolsExpanded: expandedAgents.has(agentName),
						// Enable expand toggle in both modes (different behavior per mode)
						onToolsExpandToggle: handleAgentToolsExpandToggle,
						// Disable add tool in monitoring mode
						onAddToolClick: isReadOnlyCanvas ? undefined : handleAddToolClick,
						isToolMenuOpen,
						// Add iteration data for monitoring/debug mode (when we have execution data)
						...(hasExecutionData && {
							iterationCount,
							currentIteration,
							onIterationChange,
						}),
					},
				};
			}

			// Inject iteration data for foreach nodes in monitoring mode.
			// The loop-body add affordance now lives on the TerminalEdge.
			if (node.type === "foreach") {
				const scopeName = node.data.actionName;
				const iterationCount = hasExecutionData ? execution?.iterationCount : undefined;
				const currentScopeIteration = stableScopeIterations[scopeName] ?? 0;

				return {
					...baseNode,
					data: {
						...baseNode.data,
						// Monitoring mode iteration data
						...(iterationCount !== undefined && { iterationCount }),
						...(iterationCount !== undefined && iterationCount > 1 && {
							currentIteration: currentScopeIteration,
							onIterationChange: onScopeIterationChange,
						}),
					},
				};
			}

			// Inject condition summary and branch add callbacks for if nodes.
			// True/false branches use hover "+" buttons (like foreach loop) when
			// empty; drag-and-drop from the branch handles is still supported.
			// The terminal "After" (runAfter) edge from the merge handle is
			// rendered separately by the terminalHandles logic above.
			if (node.type === "if") {
				const actionName = node.data.actionName;

				// Get condition summary from the workflow action's expression
				// The expression can be at action.inputs.expression (structured) or action.expression (legacy)
				const action = workflowActions?.[actionName];
				const rawExpression = action && 'inputs' in action && (action as Record<string, unknown>).inputs
					? ((action as Record<string, unknown>).inputs as Record<string, unknown>)?.expression
					: action && 'expression' in action
						? (action as Record<string, unknown>).expression
						: undefined;
				const conditionSummary = rawExpression ? getConditionSummary(rawExpression) : undefined;

				return {
					...baseNode,
					data: {
						...baseNode.data,
						...(conditionSummary && { conditionSummary }),
					},
				};
			}

			// Inject branch taken info for If nodes in debug mode
			if (node.type === "if" && hasExecutionData && execution?.output) {
				// Determine which branch was taken from output
				// Logic Apps typically stores the condition result in output
				let branchTaken: 'true' | 'false' | undefined;
				try {
					const output = execution.output as Record<string, unknown>;
					// Check various possible output formats for condition result
					if ('expressionResult' in output) {
						branchTaken = output.expressionResult === true ? 'true' : 'false';
					} else if ('evaluationResult' in output) {
						branchTaken = output.evaluationResult === true ? 'true' : 'false';
					} else if ('body' in output && typeof output.body === 'object' && output.body !== null) {
						const body = output.body as Record<string, unknown>;
						if ('expression' in body) {
							branchTaken = body.expression === true ? 'true' : 'false';
						}
					}
				} catch {
					// Ignore parsing errors
				}

				if (branchTaken) {
					return {
						...baseNode,
						data: {
							...baseNode.data,
							branchTaken,
						},
					};
				}
			}

			return baseNode;
		});

		// Create a map for quick lookup of processedGraphNodes by ID
		// This ensures we use the same graphNodes that terminalNodeIds was computed from
		const processedNodeMap = new Map(processedGraphNodes.map((n) => [n.id, n]));

		// Use callback form of setNodes to access current nodes with measured dimensions
		// without adding nodes to the dependency array (which would cause infinite loops)
		setNodes((currentNodes) => {
			// Calculate phantom nodes using measured dimensions from current nodes state
			// This ensures we use accurate dimensions after ReactFlow has measured the nodes
			// Skip phantom nodes in monitoring mode - they're only for add button targets
			// Create one phantom node per terminal handle (control flow nodes may have multiple)
			const phantomNodes = isReadOnlyCanvas ? [] : terminalHandles.map((th, _idx) => {
				// Try to find the node in current nodes state (has measured dimensions)
				// Use processedNodeMap for the graph node - this is guaranteed to be in sync
				// with terminalHandles since both are derived from the same graphNodes
				const currentNode = currentNodes.find((n) => n.id === th.nodeId);
				const graphNode = processedNodeMap.get(th.nodeId);

				if (!graphNode) {
					// This shouldn't happen if everything is in sync, but log for debugging
					console.warn(`[WorkflowDesigner] Phantom node skipped: graphNode not found for ${th.nodeId}`);
					return null;
				}

				// Use measured dimensions from current state, fallback to defaults
				const nodeWidth = currentNode?.measured?.width ?? DEFAULT_NODE_WIDTH;
				const nodeHeight = currentNode?.measured?.height ?? DEFAULT_NODE_HEIGHT;

				// Compute handle index/count for this node to spread phantoms apart
				const nodeHandles = terminalHandles.filter((h) => h.nodeId === th.nodeId);
				const handleIndex = nodeHandles.indexOf(th);
				const handleCount = nodeHandles.length;

				// Use position from graphNodes (updated from workflow store).
				// For scoped branch terminals (if true/false, foreach loop, switch cases)
				// the phantom is placed at the exact spot where a new action would land,
				// so the visible "+" coincides with the natural drop zone for that branch.
				const position = computeTerminalPhantomPosition(
					graphNode.position,
					nodeWidth,
					nodeHeight,
					isHorizontal,
					th.handle,
					th.nodeType,
					th.actionName,
					handleIndex,
					handleCount,
				);

				return {
					id: `terminal-target-${th.nodeId}-${th.handle}`,
					type: "phantom",
					position,
					data: {},
					selectable: false,
					draggable: false,
				};
			}).filter(Boolean);

			// Add placeholder trigger node if there are actions but no triggers (design mode only)
			const placeholderTriggerNodes: GraphNode[] = [];
			if (!isReadOnlyCanvas && !hasTriggers && processedGraphNodes.length > 0) {
				// Check if placeholder already exists to preserve its position
				const existingPlaceholder = currentNodes.find(n => n.id === 'placeholder-trigger');
				// Position at center of canvas (same as where new triggers are added)
				const placeholderPosition = existingPlaceholder?.position ?? DEFAULT_TRIGGER_POSITION;

				placeholderTriggerNodes.push({
					id: 'placeholder-trigger',
					type: 'placeholderTrigger',
					position: placeholderPosition,
					data: {
						label: 'Add a trigger',
						actionType: 'placeholder',
						actionName: 'placeholder-trigger',
						onClick: handlePlaceholderTriggerClick,
					},
					selectable: false,
					draggable: false,
				});
			}

			const result = [
				...placeholderTriggerNodes,
				...processedGraphNodes,
				// Tool nodes with selection state, execution data, and callbacks
				...toolNodes.map((toolNode) => {
					// Get execution data for tool nodes in monitoring or debug mode
					const toolExecution = hasExecutionData
						? executionLookup.get(toolNode.id) ?? executionLookup.get(`action-${toolNode.data.actionName}`)
						: undefined;

					const isToolSelected = isMonitoringMode
						? toolNode.id === monitoringSelectedNodeId
						: selectedNode?.id === toolNode.id;

					const agentName = toolNode.data.parentAgentName;

					return {
						...toolNode,
						selected: isToolSelected,
						draggable: !isReadOnlyCanvas && !isCanvasLocked,
						data: {
							...toolNode.data,
							...(toolExecution && {
								executionStatus: toolExecution.status,
								executionDuration: toolExecution.duration,
							}),
							// Add callbacks (only in design mode)
							...(!isReadOnlyCanvas && agentName && {
								onConfigure: (toolName: string) => handleToolConfigure(toolName, agentName),
								onDuplicate: (toolName: string) => handleToolDuplicate(toolName, agentName),
							}),
						},
					};
				}),
				// Model nodes (below agents)
				...modelNodes,
				...phantomNodes,
				...noteNodes,
			] as GraphNode[];

			// Size scope containers based on child node positions
			const sizedResult = applyScopeContainerBounds(
				result.map((n) => {
					const current = currentNodes.find((cn) => cn.id === n.id);
					return current?.measured
						? { ...n, measured: current.measured }
						: n;
				})
			);

			// Safety check: Don't clear workflow nodes if we previously had them
			// Count workflow nodes (triggers, actions) in both current and new arrays
			const currentWorkflowNodeCount = currentNodes.filter(
				(n) => n.id.startsWith('trigger-') || n.id.startsWith('action-')
			).length;
			const newWorkflowNodeCount = sizedResult.filter(
				(n) => n.id.startsWith('trigger-') || n.id.startsWith('action-')
			).length;

			// If we're going from having workflow nodes to having none, but workflow has content, skip this update
			if (currentWorkflowNodeCount > 0 && newWorkflowNodeCount === 0 && workflowHasContent) {
				return currentNodes;
			}

			// Also block if we had nodes but now have none and we have notes
			if (currentWorkflowNodeCount > 0 && newWorkflowNodeCount === 0 && workflowHasNotes) {
				return currentNodes;
			}

			return sizedResult;
		});

		// Use refs to ensure we have the latest edge data
		// This prevents stale closure issues similar to the graphNodes issue
		const latestEdgesWithHandlers = graphEdgesRef.current.map((edge) => {
			// Look up execution for the target node.
			// Fall back to action-name lookup when edge.target is a scoped ID
			// (e.g., "action-IsApproved/Compose_1") that does not match directly in the
			// executionLookup (which uses unscoped IDs like "action-Compose_1").
			let targetExecution: NodeExecution | undefined;
			if (hasExecutionData) {
				targetExecution = executionLookup.get(edge.target);
				if (!targetExecution) {
					const targetNode = processedNodeMap.get(edge.target);
					if (targetNode) {
						targetExecution = executionLookup.get(`action-${targetNode.data.actionName}`);
					}
				}
			}
			const isTargetRunning = targetExecution?.status === 'running';

			if (edge.type === "dotted") {
				if (
					!isReadOnlyCanvas &&
					(edge.sourceHandle === 'true' || edge.sourceHandle === 'false' || edge.sourceHandle === 'loop')
				) {
					const sourceNode = processedNodeMap.get(edge.source);
					const parentActionName = sourceNode?.data?.actionName as string | undefined;
					if (parentActionName) {
						const branchType = edge.sourceHandle as 'true' | 'false' | 'loop';
						return {
							...edge,
							type: 'dotted' as const,
							animated: isTargetRunning,
							data: {
								...(edge.data ?? {}),
								onAddClick: (sourceId: string, targetId: string) =>
									handleBranchEdgeAddClick(sourceId, targetId, branchType, parentActionName),
							},
						};
					}
				}
				return { ...edge, type: "dotted" as const, animated: isTargetRunning };
			}
			if (isReadOnlyCanvas) {
				return { ...edge, animated: isTargetRunning };
			}
			return {
				...edge,
				type: "addButton" as const,
				animated: isTargetRunning,
				data: { onAddClick: handleEdgeAddClick, onAddAgentClick: handleEdgeAddAgent },
			};
		});

		// Add placeholder trigger edge if there are actions but no triggers
		// Connect to root actions (those with no runAfter dependencies)
		const placeholderEdges: Edge[] = [];
		if (!isReadOnlyCanvas && !hasTriggers && processedGraphNodes.length > 0) {
			// Find root actions - those with empty or no runAfter (would run first after trigger)
			const actions = workflow?.definition?.actions ?? {};
			const rootActionNames = Object.entries(actions)
				.filter(([, action]) => {
					const runAfter = action.runAfter;
					return !runAfter || Object.keys(runAfter).length === 0;
				})
				.map(([name]) => name);

			// Create edges to all root actions
			rootActionNames.forEach((actionName, index) => {
				const targetNode = processedGraphNodes.find(n => n.data?.actionName === actionName);
				if (targetNode) {
					placeholderEdges.push({
						id: `placeholder-trigger-edge${index > 0 ? `-${index}` : ''}`,
						source: 'placeholder-trigger',
						target: targetNode.id,
						sourceHandle: 'output',
						targetHandle: 'input',
						type: 'dotted',
					});
				}
			});
		}

		// Compute the new edges array
		const newEdges = [
			...placeholderEdges,
			...latestEdgesWithHandlers,
			...toolEdges,
			...modelEdges,
			...terminalEdges,
		];

		// Safety check: Don't clear edges if we previously had edges and the workflow
		// still legitimately expects edges. The expected-edge check avoids the
		// false positive where the user just deleted the last edge and every action
		// is now detached (issue #955 followup): in that case zero edges is correct.
		if (newEdges.length === 0 && lastKnownEdgeCountRef.current > 0 && expectedEdgeCount > 0) {
			// Suspicious: we had edges before but now computing empty - skip this sync
			return;
		}

		// Update the last known edge count
		lastKnownEdgeCountRef.current = newEdges.length;

		setEdges(newEdges);
	}, [graphNodes, graphEdges, terminalNodeIds, terminalHandles, toolNodes, toolEdges, modelNodes, modelEdges, terminalEdges, setNodes, setEdges, selectedNode, expandedAgents, handleAgentToolsExpandToggle, handleAddToolClick, handleEdgeAddClick, handleEdgeAddAgent, handleToolConfigure, handleToolDuplicate, handleNodeDelete, handleNodeEditWithAI, handleNodeDuplicate, handleNodeOpen, handlePlaceholderTriggerClick, layoutOrientation, workflow, isMonitoringMode, isReadOnlyCanvas, isCanvasLocked, monitoringSelectedNodeId, executionLookup, hasExecutionData, stableAgentIterations, onIterationChange, stableScopeIterations, onScopeIterationChange, handleScopedAddClick, handleBranchEdgeAddClick, hasTriggers, dirtyNodeIds, isExecutionMode, noteNodes, workflowActions, toolTypeMenuState.open, toolTypeMenuState.targetAgentName]);

	const handleNodesChange = useCallback(
		(changes: NodeChange<Node>[]) => {
			// Collect phantom node position updates for nodes being dragged or resized
			const phantomUpdates: { id: string; position: { x: number; y: number } }[] = [];
			const isHorizontal = layoutOrientation === "horizontal";

			// Track dragging state and update positions in workflow when drag ends
			changes.forEach((change) => {
				if (change.type === "position") {
					// Track if any node is being dragged
					if (change.dragging === true) {
						isDraggingRef.current = true;

						// If this node has phantom nodes, calculate their new positions
						if (change.position) {
							// Find the current node to get its dimensions
							// Use nodesRef.current instead of nodes to avoid recreation of this callback
							const node = nodesRef.current.find((n) => n.id === change.id);
							if (node) {
								const nodeWidth = node.measured?.width ?? DEFAULT_NODE_WIDTH;
								const nodeHeight = node.measured?.height ?? DEFAULT_NODE_HEIGHT;

								// Update all phantom nodes for this node's terminal handles
								const nodeHandles = terminalHandles.filter((th) => th.nodeId === change.id);
								nodeHandles.forEach((th, idx) => {
									const handlePhantomPosition = computeTerminalPhantomPosition(
										change.position!,
										nodeWidth,
										nodeHeight,
										isHorizontal,
										th.handle,
										th.nodeType,
										th.actionName,
										idx,
										nodeHandles.length,
									);
									phantomUpdates.push({ id: `terminal-target-${change.id}-${th.handle}`, position: handlePhantomPosition });
								});
							}
						}
					} else if (change.dragging === false) {
						isDraggingRef.current = false;
						// Set settling flag to skip the next effect run
						// This prevents race conditions where edges disappear due to
						// the effect running before memoized values have updated
						postDragSettlingRef.current = true;

						// Update position in workflow when drag ends
						if (change.position) {
							const nodeId = change.id;
							// Round to whole numbers for cleaner JSON
							const roundedPosition = {
								x: Math.round(change.position.x),
								y: Math.round(change.position.y),
							};

							if (nodeId.startsWith("trigger-")) {
								const triggerName = nodeId.replace("trigger-", "");
								updateTriggerPosition(triggerName, roundedPosition);
							} else if (nodeId.startsWith("action-")) {
								const actionName = nodeId.replace("action-", "");
								updateActionPosition(actionName, roundedPosition);
							} else if (nodeId.startsWith("note-")) {
								const noteId = nodeId.replace("note-", "");
								updateNote(noteId, { metadata: { position: roundedPosition } } as Partial<Note>);
							} else if (nodeId.startsWith("tool-")) {
								// Tool nodes carry their agent + tool name in data;
								// the node id encodes the inner action name, not the tool name.
								const node = nodesRef.current.find((n) => n.id === nodeId);
								const { parentAgentName, toolName } = (node?.data ?? {}) as { parentAgentName?: string; toolName?: string };
								if (parentAgentName && toolName) {
									updateToolNodePosition(parentAgentName, toolName, roundedPosition);
								}
							}
						}
					}
				}

				// Handle dimension changes (when ReactFlow measures nodes after render)
				// This ensures phantom nodes are positioned correctly on initial mount
				if (change.type === "dimensions" && change.dimensions) {
					const nodeId = change.id;
					// Only update phantom for terminal nodes (those that have phantom targets)
					if (terminalNodeIds.has(nodeId)) {
						// Use nodesRef.current instead of nodes to avoid recreation of this callback
						const node = nodesRef.current.find((n) => n.id === nodeId);
						if (node) {
							// Update all phantom nodes for this terminal node's handles
							const nodeHandles = terminalHandles.filter((th) => th.nodeId === nodeId);
							nodeHandles.forEach((th, idx) => {
								const handlePhantomPosition = computeTerminalPhantomPosition(
									node.position,
									change.dimensions!.width,
									change.dimensions!.height,
									isHorizontal,
									th.handle,
									th.nodeType,
									th.actionName,
									idx,
									nodeHandles.length,
								);
								phantomUpdates.push({ id: `terminal-target-${nodeId}-${th.handle}`, position: handlePhantomPosition });
							});
						}
					}
				}
			});

			// Filter out 'remove' changes - we handle node deletion explicitly via context menu/delete button
			// This prevents nodes from being accidentally deleted through ReactFlow's internal mechanisms
			// Similar to how we protect edges in handleEdgesChange
			// Also filter out position/select changes for scope container nodes — they are purely visual
			// and should only be repositioned by the bounds calculation, not by user interaction
			const safeChanges = changes.filter((change) => {
				if (change.type === 'remove') return false;
				// Scope container nodes are purely visual — only bounds calculation should reposition them
				if ((change.type === 'position' || change.type === 'select')
					&& 'id' in change && typeof change.id === 'string'
					&& change.id.startsWith('scope-container-')) return false;
				return true;
			});

			// Apply safe changes
			if (safeChanges.length > 0) {
				onNodesChange(safeChanges as NodeChange<GraphNode>[]);
			}

			// Then update phantom nodes if any need updating
			if (phantomUpdates.length > 0) {
				setNodes((currentNodes) =>
					currentNodes.map((node) => {
						const update = phantomUpdates.find((u) => u.id === node.id);
						if (update) {
							return { ...node, position: update.position };
						}
						return node;
					})
				);
			}

			// Recalculate scope container bounds when child nodes are dragged or get measured
			const needsBoundsUpdate = changes.some(
				(c) => (c.type === 'position' && 'dragging' in c && c.dragging) || c.type === 'dimensions'
			);
			if (needsBoundsUpdate) {
				setNodes(applyScopeContainerBounds);
			}
		},
		[onNodesChange, updateActionPosition, updateTriggerPosition, updateToolNodePosition, updateNote, layoutOrientation, setNodes, terminalNodeIds, terminalHandles],
	);

	// Monitoring-mode node-change handler: forwards measurement (dimension) updates
	// to ReactFlow's internal store and re-runs scope-container bounds so loop
	// containers size to the actual measured children instead of the default
	// fallback (200×120 → 280×228 dashed box). User-driven changes (drag/select
	// /remove) are filtered out because monitoring is read-only.
	const handleMonitoringNodesChange = useCallback(
		(changes: NodeChange<Node>[]) => {
			const dimensionChanges = changes.filter((c) => c.type === 'dimensions');
			if (dimensionChanges.length === 0) return;
			onNodesChange(dimensionChanges as NodeChange<GraphNode>[]);
			setNodes(applyScopeContainerBounds);
		},
		[onNodesChange, setNodes],
	);

	// Wrapper for onEdgesChange that filters out unexpected remove events
	// This prevents edges from being accidentally deleted through ReactFlow's internal mechanisms
	// We only allow edge removals through our explicit handlers (context menu delete)
	const handleEdgesChange = useCallback(
		(changes: EdgeChange<Edge>[]) => {
			// Filter out 'remove' changes - we handle edge deletion explicitly via context menu
			const safeChanges = changes.filter((change) => change.type !== 'remove');
			if (safeChanges.length > 0) {
				onEdgesChange(safeChanges);
			}
		},
		[onEdgesChange],
	);

	const handlePaneContextMenu = useCallback(
		(event: React.MouseEvent | MouseEvent) => {
			event.preventDefault();
			telemetryService.trackEvent('workflow.context_menu.opened', { target: 'pane' });

			// Convert screen coordinates to flow coordinates for node placement
			const flowPosition = screenToFlowPosition({
				x: event.clientX,
				y: event.clientY,
			});

			// Store both screen position (for context menu) and flow position (for node creation)
			setContextMenuPosition({ x: event.clientX, y: event.clientY });
			setClickPosition(flowPosition);
			setIsContextMenuOpen(true);
		},
		[screenToFlowPosition],
	);

	const handleCloseContextMenu = useCallback(() => {
		setIsContextMenuOpen(false);
	}, []);

	// Handle clicking on the canvas (not on a node) - close context menus and collapse sub-workflow preview
	const handlePaneClick = useCallback(() => {
		closeAllContextMenus();
		setExpandedSubWorkflow(null);
	}, [closeAllContextMenus]);

	const handleAddTriggerFromMenu = useCallback(() => {
		telemetryService.trackEvent('workflow.node.added', { nodeType: 'trigger' });
		closeAllContextMenus();
		setPanelState({
			isOpen: true,
			mode: "trigger",
			insertionPoint: null,
		});
	}, [closeAllContextMenus]);

	const handleAddActionFromMenu = useCallback(() => {
		telemetryService.trackEvent('workflow.node.added', { nodeType: 'action' });
		closeAllContextMenus();
		setPanelState({
			isOpen: true,
			mode: "action",
			insertionPoint: null,
		});
	}, [closeAllContextMenus]);

	const handleAddNoteFromMenu = useCallback(() => {
		telemetryService.trackEvent('workflow.node.added', { nodeType: 'note' });
		closeAllContextMenus();
		if (clickPosition) {
			addNote(clickPosition);
		}
	}, [closeAllContextMenus, clickPosition, addNote]);

	const handleAddNoteFromDock = useCallback(() => {
		telemetryService.trackEvent('workflow.node.added', { nodeType: 'note' });
		// Add note at center of visible canvas
		const container = canvasContainerRef.current;
		if (container) {
			const rect = container.getBoundingClientRect();
			const center = screenToFlowPosition({
				x: rect.left + rect.width / 2,
				y: rect.top + rect.height / 2,
			});
			addNote(center);
		}
	}, [screenToFlowPosition, addNote]);

	// Toolbox handlers - these close and reopen the panel to reset it
	const handleBrowseActions = useCallback(() => {
		closeAllContextMenus();
		// Close first, then reopen to reset the panel
		setPanelState((prev) => {
			if (prev.isOpen && prev.mode === "action") {
				// Already open in action mode - close and reopen
				setTimeout(() => {
					setPanelState({
						isOpen: true,
						mode: "action",
						insertionPoint: null,
					});
				}, 0);
				return { isOpen: false, mode: "action", insertionPoint: null };
			}
			return {
				isOpen: true,
				mode: "action",
				insertionPoint: null,
			};
		});
	}, [closeAllContextMenus]);

	const handleBrowseTriggers = useCallback(() => {
		closeAllContextMenus();
		setPanelState((prev) => {
			if (prev.isOpen && prev.mode === "trigger") {
				// Already open in trigger mode - close and reopen
				setTimeout(() => {
					setPanelState({
						isOpen: true,
						mode: "trigger",
						insertionPoint: null,
					});
				}, 0);
				return { isOpen: false, mode: "trigger", insertionPoint: null };
			}
			return {
				isOpen: true,
				mode: "trigger",
				insertionPoint: null,
			};
		});
	}, [closeAllContextMenus]);

	// Handle MCP browse from toolbox - opens MCP panel to add tool to new agent
	const handleBrowseMCP = useCallback(() => {
		closeAllContextMenus();
		// Open MCP panel with no target agent - will create new agent when tool is selected
		setPanelState((prev) => ({ ...prev, isOpen: false }));
		setAuxPickerView({ kind: 'mcp', targetAgentName: null });
	}, [closeAllContextMenus]);

	// Handle Sub-workflow browse from toolbox - opens subworkflow picker panel
	const handleBrowseSubworkflow = useCallback(() => {
		closeAllContextMenus();
		// Ensure the operation picker panel is closed before opening the subworkflow panel
		setPanelState((prev) => ({ ...prev, isOpen: false }));
		setAuxPickerView({ kind: 'subworkflow', targetAgentName: null });
	}, [closeAllContextMenus]);

	// Handle Templates browse from toolbar - opens template gallery panel
	const handleBrowseTemplates = useCallback(() => {
		setInitialPreviewTemplateId(undefined);
		setIsTemplateGalleryOpen(true);
	}, []);

	const handleTemplateGalleryDismiss = useCallback(() => {
		setIsTemplateGalleryOpen(false);
		setInitialPreviewTemplateId(undefined);
	}, []);

	// Handle "Back to start" - closes menus (empty state shown automatically when canvas is empty)
	const handleBackToStart = useCallback(() => {
		closeAllContextMenus();
	}, [closeAllContextMenus]);

	const handleConnect = useCallback(
		(connection: Connection) => {
			if (connection.source && connection.target) {
				telemetryService.trackEvent('workflow.edge.connected');
				// Prevent self-loops (node connecting to itself)
				if (connection.source === connection.target) {
					console.warn('[WorkflowDesigner] Prevented self-loop edge:', connection.source);
					return;
				}
				// Defense-in-depth: re-check that this scoped branch handle doesn't
				// already have a direct child. isValidConnection should have caught
				// this during drag, but guard here too in case onConnect fires from
				// a code path that bypassed validation.
				const sourceHandle = connection.sourceHandle ?? null;
				if (sourceHandle) {
					const sourceNode = nodes.find((n) => n.id === connection.source);
					const sourceType = sourceNode?.type;
					const isScopedBranchHandle =
						(sourceType === 'foreach' && sourceHandle === 'loop') ||
						(sourceType === 'if' && (sourceHandle === 'true' || sourceHandle === 'false')) ||
						(sourceType === 'switch' && (sourceHandle.startsWith('case-') || sourceHandle === 'default'));
					if (isScopedBranchHandle) {
						const hasExistingChild = edges.some(
							(e) =>
								e.source === connection.source &&
								(e.sourceHandle ?? null) === sourceHandle &&
								!e.target.startsWith('terminal-target-'),
						);
						if (hasExistingChild) {
							console.warn(
								'[WorkflowDesigner] Rejected sibling on scoped branch handle:',
								connection.source,
								sourceHandle,
							);
							return;
						}
					}
				}
				addEdge({ source: connection.source, handle: connection.sourceHandle }, { target: connection.target, handle: connection.targetHandle });
			}
		},
		[addEdge, nodes, edges],
	);

	// Block cross-scope connections involving ForEach children
	const isValidConnection = useCallback(
		(connection: GraphEdge | Connection) => {
			if (!connection.source || !connection.target) return false;

			const sourceNode = nodes.find((n) => n.id === connection.source);
			const targetNode = nodes.find((n) => n.id === connection.target);
			if (!sourceNode || !targetNode) return false;

			const sourceScope = sourceNode.data?.scopeParentId;
			const targetScope = targetNode.data?.scopeParentId;
			const sourceInLoop = !!(sourceScope && sourceNode.data?.branchType === 'loop');
			const targetInLoop = !!(targetScope && targetNode.data?.branchType === 'loop');

			// Actions inside a ForEach loop can only connect to nodes in the same scope
			if (sourceInLoop && sourceScope !== targetScope) return false;

			// Nodes outside a ForEach can only enter it if they ARE the ForEach node itself
			if (targetInLoop && targetScope !== sourceScope && connection.source !== targetScope) return false;

			// A scoped branch handle (If true/false, ForEach loop, Switch case/default)
			// can only have one direct child action. If an outgoing edge already exists
			// from this source+handle pair, reject the new connection — the user should
			// chain the new action via runAfter on the existing root instead.
			const sourceHandle = connection.sourceHandle ?? null;
			if (sourceHandle) {
				const sourceType = sourceNode.type;
				const isScopedBranchHandle =
					(sourceType === 'foreach' && sourceHandle === 'loop') ||
					(sourceType === 'if' && (sourceHandle === 'true' || sourceHandle === 'false')) ||
					(sourceType === 'switch' && (sourceHandle.startsWith('case-') || sourceHandle === 'default'));
				if (isScopedBranchHandle) {
					const hasExistingChild = edges.some(
						(e) =>
							e.source === connection.source &&
							(e.sourceHandle ?? null) === sourceHandle &&
							!e.target.startsWith('terminal-target-'),
					);
					if (hasExistingChild) return false;
				}
			}

			return true;
		},
		[nodes, edges],
	);

	// Handle right-click on nodes
	const handleNodeContextMenu = useCallback(
		(event: React.MouseEvent, node: Node) => {
			event.preventDefault();
			event.stopPropagation();

			// Don't show context menu for phantom/terminal nodes or placeholder trigger
			if (node.id.startsWith('terminal-target-') || node.id === 'placeholder-trigger' || node.hidden) {
				return;
			}

			// Clear any selected node to close the info panel
			clearSelection();

			// Handle note nodes separately
			if (node.id.startsWith('note-')) {
				telemetryService.trackEvent('workflow.context_menu.opened', { target: 'note' });
				const noteId = node.id.replace('note-', '');
				const note = workflow?.notes?.[noteId];
				setNoteContextMenuPosition({ x: event.clientX, y: event.clientY });
				setContextMenuNoteId(noteId);
				setContextMenuNoteColor(note?.color ?? null);
				setIsNoteContextMenuOpen(true);
				// Close other menus
				setIsContextMenuOpen(false);
				setIsNodeContextMenuOpen(false);
				setIsEdgeContextMenuOpen(false);
				return;
			}

			setNodeContextMenuPosition({ x: event.clientX, y: event.clientY });
			setContextMenuNodeId(node.id);

			// Determine node type
			let nodeType: 'trigger' | 'action' | 'tool';
			if (node.id.startsWith('trigger-')) {
				nodeType = 'trigger';
			} else if (node.id.startsWith('tool-')) {
				nodeType = 'tool';
			} else {
				nodeType = 'action';
			}
			telemetryService.trackEvent('workflow.context_menu.opened', { target: 'node' });
			setContextMenuNodeType(nodeType);
			setIsNodeContextMenuOpen(true);

			// Close other menus
			setIsContextMenuOpen(false);
			setIsNoteContextMenuOpen(false);
			setIsEdgeContextMenuOpen(false);
		},
		[clearSelection, workflow?.notes]
	);

	const handleCloseNodeContextMenu = useCallback(() => {
		setIsNodeContextMenuOpen(false);
		setContextMenuNodeId(null);
		setContextMenuNodeType(null);
	}, []);

	// Note context menu handlers
	const handleCloseNoteContextMenu = useCallback(() => {
		setIsNoteContextMenuOpen(false);
		setContextMenuNoteId(null);
		setContextMenuNoteColor(null);
	}, []);

	const handleDeleteNoteFromContextMenu = useCallback(() => {
		if (!contextMenuNoteId) return;
		deleteNote(contextMenuNoteId);
		handleCloseNoteContextMenu();
	}, [contextMenuNoteId, deleteNote, handleCloseNoteContextMenu]);

	const handleDuplicateNote = useCallback(() => {
		if (!contextMenuNoteId || !workflow?.notes) return;
		const note = workflow.notes[contextMenuNoteId];
		if (!note) return;
		// Create a new note with same content/color but offset position
		const newPosition = {
			x: note.metadata.position.x + 20,
			y: note.metadata.position.y + 20,
		};
		const newId = addNote(newPosition);
		// Update the new note with the same content, color, and dimensions
		updateNote(newId, {
			content: note.content,
			color: note.color,
			metadata: {
				position: newPosition,
				width: note.metadata.width,
				height: note.metadata.height,
			},
		});
		handleCloseNoteContextMenu();
	}, [contextMenuNoteId, workflow?.notes, addNote, updateNote, handleCloseNoteContextMenu]);

	const handleChangeNoteColor = useCallback((color: string) => {
		if (!contextMenuNoteId) return;
		updateNote(contextMenuNoteId, { color });
		handleCloseNoteContextMenu();
	}, [contextMenuNoteId, updateNote, handleCloseNoteContextMenu]);

	const handleDeleteNode = useCallback(() => {
		if (!contextMenuNodeId) return;
		telemetryService.trackEvent('workflow.node.deleted', { nodeType: contextMenuNodeType ?? 'unknown' });

		if (contextMenuNodeType === 'trigger') {
			const nodeName = contextMenuNodeId.replace(/^trigger-/, '');
			deleteTrigger(nodeName);
		} else if (contextMenuNodeType === 'tool') {
			// Look up the node to get toolName and parentAgentName from data
			// Node ID format uses innerActionName which may differ from toolName
			const toolNode = nodes.find(n => n.id === contextMenuNodeId);
			const toolData = toolNode?.data as { toolName?: string; parentAgentName?: string } | undefined;
			const agentName = toolData?.parentAgentName;
			const toolName = toolData?.toolName;
			if (agentName && toolName) {
				deleteToolFromAgent(agentName, toolName);
			}
		} else if (contextMenuNodeType === 'action') {
			const nodeName = contextMenuNodeId.replace(/^action-/, '');
			deleteAction(nodeName);
		}

		handleCloseNodeContextMenu();
	}, [contextMenuNodeId, contextMenuNodeType, deleteAction, deleteTrigger, deleteToolFromAgent, handleCloseNodeContextMenu, nodes]);

	// Handle "Edit with AI Assistant" from context menu
	// Selects the node (which automatically scopes the AI) and opens the chat sidebar
	const handleEditWithAssistant = useCallback(() => {
		if (!contextMenuNodeId || !contextMenuNodeType) return;

		if (contextMenuNodeType === 'tool') {
			// Tool node ID format: tool-{agentName}-{toolName}
			const parts = contextMenuNodeId.replace(/^tool-/, '').split('-');
			if (parts.length >= 2) {
				const agentName = parts[0]!;
				const toolName = parts.slice(1).join('-'); // Handle names with dashes
				selectTool(agentName, toolName);
			}
		} else {
			const nodeName = contextMenuNodeId.replace(/^(trigger-|action-)/, '');
			// Select the node - this automatically scopes AI to this node
			selectNode({
				id: contextMenuNodeId,
				type: contextMenuNodeType,
				name: nodeName,
			});
		}

		// Open the chat sidebar if it's not already open
		if (!isChatSidebarOpen && onToggleChat) {
			onToggleChat();
		}

		handleCloseNodeContextMenu();
	}, [contextMenuNodeId, contextMenuNodeType, selectNode, selectTool, handleCloseNodeContextMenu, isChatSidebarOpen, onToggleChat]);

	// Handle right-click on edges
	const handleEdgeContextMenu = useCallback(
		(event: React.MouseEvent, edge: Edge) => {
			event.preventDefault();
			event.stopPropagation();

			// Don't show context menu for terminal edges
			if (edge.id.startsWith('terminal-')) {
				return;
			}

			telemetryService.trackEvent('workflow.context_menu.opened', { target: 'edge' });
			setEdgeContextMenuPosition({ x: event.clientX, y: event.clientY });
			setContextMenuEdgeId(edge.id);
			setContextMenuEdgeSourceId(edge.source);
			setContextMenuEdgeIsScopeEdge(edge.type === 'dotted');
			setContextMenuEdgeSourceHandle(edge.sourceHandle ?? null);
			setIsEdgeContextMenuOpen(true);

			// Close other menus
			setIsContextMenuOpen(false);
			setIsNodeContextMenuOpen(false);
		},
		[]
	);

	const handleCloseEdgeContextMenu = useCallback(() => {
		setIsEdgeContextMenuOpen(false);
		setContextMenuEdgeId(null);
		setContextMenuEdgeSourceId(null);
		setContextMenuEdgeIsScopeEdge(false);
		setContextMenuEdgeSourceHandle(null);
	}, []);

	const handleDeleteEdge = useCallback(() => {
		if (!contextMenuEdgeId) return;

		// Parse edge ID to get source and target
		// Format: "source-id->target-id"
		const parts = contextMenuEdgeId.split('->');

		if (parts.length === 2 && parts[0] && parts[1]) {
			const sourceId = parts[0];
			const targetId = parts[1];
			removeEdge(sourceId, targetId, contextMenuEdgeSourceHandle);
		}

		handleCloseEdgeContextMenu();
	}, [contextMenuEdgeId, contextMenuEdgeSourceHandle, removeEdge, handleCloseEdgeContextMenu]);

	const handleMove = useCallback(() => {
		// Pan/zoom only dismisses positionally-anchored UI (context menus).
		// The Add-an-action / picker panel docks to the side and shouldn't
		// disappear when the user scrolls around the canvas — matches the
		// behavior of the agent, MCP, sub-workflow, and connection-creation
		// panels.
		closeAllContextMenus();
	}, [closeAllContextMenus]);

	const handleOrientationToggle = useToggleLayoutOrientation();

	// Handle Tab key to open add node panel at viewport center
	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			// Defer to text editors / IME — Tab in inputs/CodeMirror/etc.
			// should advance focus or insert indent, not open the add-node
			// panel.
			if (shouldSkipShortcut(event)) return;
			if (isReadOnlyCanvas) return;
			if (event.key === "Tab" && !panelState.isOpen && !isNodeEditorOpen) {
				event.preventDefault();
				if (event.repeat) return;

				// Calculate center of viewport in flow coordinates
				const viewport = screenToFlowPosition({
					x: window.innerWidth / 2,
					y: window.innerHeight / 2,
				});

				closeAllContextMenus();
				setClickPosition(viewport);
				setPanelState({
					isOpen: true,
					mode: hasTriggers ? "action" : "trigger",
					insertionPoint: null,
				});
			}
		};

		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [panelState.isOpen, isNodeEditorOpen, screenToFlowPosition, closeAllContextMenus, hasTriggers, isReadOnlyCanvas]);

	// Handle auto-layout
	const handleAutoLayout = useCallback(async () => {
		if (isLayouting || nodes.length === 0) return;

		setIsLayouting(true);
		try {
			// Filter out phantom/terminal/tool/scope-container nodes for layout calculation
			const realNodes = nodes.filter(
				(node) =>
					!node.id.startsWith("terminal-target-") &&
					!node.id.startsWith("tool-") &&
					!node.id.startsWith("scope-container-") &&
					!node.hidden,
			);
			const realEdges = edges.filter(
				(edge) =>
					!edge.id.startsWith("terminal-") && !edge.target.startsWith("tool-"),
			);

			const layoutInput = convertToLayoutInput(realNodes, realEdges);
			const direction = orientationToDirection(layoutOrientation);
			const positions = await calculateLayout(layoutInput, direction);

			if (positions.size > 0) {
				// Mark the canvas container so the transform transition CSS only
				// fires for this re-layout. Without this scope, the transition
				// would also apply during interactive drags (XY Flow updates
				// `transform` on every pointermove), causing visible cursor lag.
				// Cleared after the transition duration completes. Any prior
				// pending clear is cancelled so a fast second re-layout doesn't
				// have its animation cut short by the first timer.
				const container = canvasContainerRef.current;
				if (container) {
					if (relayoutClearTimerRef.current != null) {
						window.clearTimeout(relayoutClearTimerRef.current);
					}
					container.setAttribute("data-otto-relayout", "true");
					relayoutClearTimerRef.current = window.setTimeout(() => {
						container.removeAttribute("data-otto-relayout");
						relayoutClearTimerRef.current = null;
					}, RELAYOUT_TRANSITION_MS);
				}
				applyLayoutPositions(positions);
			}
		} catch (error) {
			console.error("Auto-layout failed:", error);
		} finally {
			setIsLayouting(false);
		}
	}, [
		isLayouting,
		nodes,
		edges,
		layoutOrientation,
		setIsLayouting,
		applyLayoutPositions,
	]);
	// Auto-format the canvas whenever the user toggles between horizontal and
	// vertical layout. Without this, flipping orientation only swaps handle
	// positions while keeping the old node coordinates, leaving the graph in
	// an awkward intermediate state until the user manually clicks "Format".
	// We skip the initial render (so loading a workflow doesn't blow away the
	// persisted layout) and skip monitoring mode (which manages its own layout
	// pass via the snapshot effect above).
	//
	// We *also* have to call updateNodeInternals on every node before running
	// the format pass. React Flow caches each handle's measured position in an
	// internal store — when our node components swap Position.Left/Right for
	// Position.Top/Bottom in response to the orientation change, that cache
	// stays stale until something forces a remeasure. Without this, edges
	// briefly route from the old handle sides while the new positions are
	// being computed and applied, producing a visible "edges snap into place"
	// lag after every toggle.
	//
	// The remeasure is deferred to the next frame so the node components
	// have committed their new handle DOM, and we guard on DOMMatrixReadOnly
	// because XY Flow's measurement code uses it (JSDOM lacks it). This
	// mirrors the terminal-handles effect's pattern.
	//
	// After the auto-layout completes we run a fitView so the user sees
	// the whole graph at the new orientation without having to pan/zoom.
	// fitView is deferred one frame so React Flow has committed the new
	// node positions before it measures the bounding box.
	const lastToggledOrientationRef = useRef(layoutOrientation);
	useEffect(() => {
		if (lastToggledOrientationRef.current === layoutOrientation) return;
		lastToggledOrientationRef.current = layoutOrientation;

		const fitAfterLayout = (): void => {
			// Defer one frame so the layout pass's `applyLayoutPositions`
			// has flushed to React Flow before fitView reads node bbox.
			// 500ms duration is the industry sweet spot for canvas
			// camera moves (Material Design "expanded transition",
			// Figma's fit-to-zoom, Linear's viewport pans): long enough
			// for the user to track the motion, short enough to stay
			// responsive. 300ms felt jarring; 800ms+ feels sluggish.
			requestAnimationFrame(() => {
				fitView({
					padding: { top: 0.1, left: 0.1, bottom: 0.1, right: 0.35 },
					duration: 500,
				});
			});
		};

		if (typeof window === 'undefined' || typeof window.DOMMatrixReadOnly !== 'function') {
			// Test environment (JSDOM): skip the remeasure pass; just kick off
			// the auto-layout. Real browser logic below is what users see.
			// Monitoring mode never mutates the store via handleAutoLayout —
			// position recompute happens in the applyInitialLayout effect
			// triggered by the fingerprint change (now keyed off
			// layoutOrientation as well).
			if (!isMonitoringMode && getNodes().length > 0) {
				void handleAutoLayout().then(fitAfterLayout);
			}
			return;
		}

		const raf = requestAnimationFrame(() => {
			const currentNodes = getNodes();
			// updateNodeInternals is required in both modes so React Flow
			// refreshes its handle-position cache for the new orientation
			// (handles physically move to the new edges via useHandleLayout).
			for (const node of currentNodes) updateNodeInternals(node.id);
			if (currentNodes.length === 0) return;
			if (isMonitoringMode) {
				// In monitoring, the applyInitialLayout effect owns the
				// relayout (fingerprint now includes layoutOrientation, so it
				// re-runs and writes new monitoringPositions). It already
				// fits the viewport on completion — we don't need to here.
				return;
			}
			void handleAutoLayout().then(fitAfterLayout);
		});
		return () => cancelAnimationFrame(raf);
	}, [layoutOrientation, isMonitoringMode, getNodes, updateNodeInternals, handleAutoLayout, fitView]);

	// Handle keyboard shortcut for auto-arrange / Format Workflow.
	// Registry's `formatWorkflow` matcher accepts both:
	//   Shift+Alt+F  (Windows/Linux — matches VS Code's Format Document)
	//   Shift+Cmd+F  (Mac — Apple's standard Format Document binding;
	//                ⌥ on Mac mutates `event.key` into a special glyph
	//                like "Ï" so the matcher keys off `event.code`)
	// Skip when focus is in an editable element so editor "Format Document"
	// (also bound to Shift+Alt+F) wins inside JSON/code editors.
	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (shouldSkipShortcut(event)) return;
			if (matchesShortcut(event, 'formatWorkflow')) {
				event.preventDefault();
				if (event.repeat) return;
				handleAutoLayout();
			}
		};

		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [handleAutoLayout]);

	// Handle Ctrl+F / Cmd+F keyboard shortcut for canvas search.
	// Registry's `canvasSearch` matcher excludes shift/alt so ⇧⌘F
	// (Format Workflow on Mac) doesn't also open canvas search — they
	// share the F key.
	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (shouldSkipShortcut(event)) return;
			const isSearchShortcut = matchesShortcut(event, 'canvasSearch');
			
			if (isSearchShortcut && !isMonitoringMode) {
				// We own Ctrl/Cmd+F on the canvas — suppress the browser
				// find-in-page dialog even on held-key repeats, but only
				// toggle the search panel on the first press.
				event.preventDefault();
				if (event.repeat) return;
				if (isSearchOpen) {
					// Route close through the same path as onClose to ensure telemetry fires
					setIsSearchOpen(false);
					telemetryService.trackEvent('canvas.search.closed', {
						trigger: 'keyboard_shortcut',
					});
				} else {
					setIsSearchOpen(true);
					telemetryService.trackEvent('canvas.search.opened', {
						trigger: 'keyboard_shortcut',
					});
				}
			}
		};

		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [isSearchOpen, isMonitoringMode]);

	// Handle copy/paste/cut keyboard shortcuts
	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (shouldSkipShortcut(event)) return;
			// Don't allow clipboard operations in monitoring (read-only) mode
			if (isReadOnlyCanvas) return;

			const isModifierKey = isPrimaryMod(event);

			// If the user has actively selected text on the page (node labels,
			// monitoring values, parameter previews, etc.), let the browser's
			// native copy/cut handle it instead of stealing the action.
			// Paste is unaffected — there's no equivalent "selected text" concept.
			const hasTextSelection =
				(event.key.toLowerCase() === 'c' || event.key.toLowerCase() === 'x') &&
				!!window.getSelection()?.toString();
			if (isModifierKey && hasTextSelection) return;

			// Copy (Ctrl+C / Cmd+C)
			if (isModifierKey && event.key.toLowerCase() === 'c' && selectedNode?.type === 'action') {
				event.preventDefault();
				if (event.repeat) return;
				const action = getAction(selectedNode.name);
				if (action) {
					copyAction(action, selectedNode.name);
				}
			}
			
			// Paste (Ctrl+V / Cmd+V)
			if (isModifierKey && event.key.toLowerCase() === 'v' && clipboardData && clipboardActionName) {
				event.preventDefault();
				if (event.repeat) return;
				// Paste from clipboard snapshot so cut+paste works after source is deleted
				const newName = pasteActionFromClipboard(clipboardData, clipboardActionName);
				if (newName) {
					selectNodeByName(newName, 'action');
				}
			}
			
			// Cut (Ctrl+X / Cmd+X)
			if (isModifierKey && event.key.toLowerCase() === 'x' && selectedNode?.type === 'action') {
				event.preventDefault();
				if (event.repeat) return;
				const action = getAction(selectedNode.name);
				if (action) {
					copyAction(action, selectedNode.name);
					deleteAction(selectedNode.name);
				}
			}
		};

		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [isReadOnlyCanvas, selectedNode, getAction, copyAction, clipboardData, clipboardActionName, pasteActionFromClipboard, selectNodeByName, deleteAction]);

	// NOTE: We intentionally do NOT auto-relayout existing workflows on
	// first render. Saved positions in workflow.json are the source of
	// truth — silently rewriting them on every load would (a) trigger
	// dirty state for read-only navigation, (b) discard manual layout
	// adjustments the user made, and (c) deviate from the pattern in
	// every comparable tool (Power Automate, n8n, Zapier, Figma).
	// New workflows (no saved positions) are handled by the
	// `applyInitialLayout` effect below; explicit re-layout is on the
	// "Format Workflow" button (handleAutoLayout). Per-node measured
	// sizes still flow into Sugiyama via convertToLayoutInput's
	// DEFAULT_NODE_WIDTH floor, so the layout engine produces the right
	// answer the first time it's invoked.

	// Auto-layout when workflow first loads or changes - only if positions are missing
	useEffect(() => {
		if (!workflow || graphNodes.length === 0) return;

		// Create a simple fingerprint of the workflow to detect changes.
		// Including `layoutOrientation` ensures that flipping orientation in
		// monitoring mode re-triggers a layout pass — the monitoring path
		// bypasses the `hasNodesWithoutPositions` short-circuit below, so a
		// changed fingerprint is what makes new positions get computed at the
		// new orientation. In design mode this is harmless because the
		// `hasNodesWithoutPositions` guard short-circuits whenever positions
		// already exist (the dedicated orientation effect above handles
		// design-mode re-layout via `handleAutoLayout`).
		const workflowFingerprint = JSON.stringify({
			triggers: Object.keys(workflow.definition.triggers || {}),
			actions: Object.keys(workflow.definition.actions || {}),
			orientation: layoutOrientation,
		});

		// Only auto-layout if this is a different workflow than we last layouted.
		// In monitoring mode we ALWAYS recompute on the frontend (the snapshot's
		// own metadata.position is intentionally ignored), so bypass the
		// hasNodesWithoutPositions short-circuit and defer setting the
		// fingerprint until layout has actually run. Otherwise a transient
		// "positions look present" render (caused by the previous run's
		// monitoringPositions still being in state for one render after a
		// snapshot change) would poison the guard and skip the real layout
		// pass on the next render.
		if (lastLayoutedWorkflowRef.current !== workflowFingerprint) {
			// Reset pending state for each new workflow so the canvas hides
			// during layout even when the component stays mounted across routes.
			setIsInitialLayoutPending(true);

			const positionsForCheck = currentWorkflowIdentity
				? useWorkflowLayoutStore.getState().getNodePositions(currentWorkflowIdentity, currentMode)
				: {};
			if (!isMonitoringMode && !hasNodesWithoutPositions(workflow, positionsForCheck)) {
				lastLayoutedWorkflowRef.current = workflowFingerprint;
				setIsInitialLayoutPending(false);
				return; // All nodes have positions, preserve them
			}

			// Apply auto-layout immediately (no delay needed - React handles batching)
			const applyInitialLayout = async () => {
				// Check isLayouting from store to avoid stale closure issues
				if (useWorkflowStore.getState().isLayouting) return;

				// Capture snapshot identity at start. calculateLayout is async; if
				// the user switches runs while it's in flight, the resolved
				// positions belong to the previous snapshot and must be dropped
				// to avoid painting them onto the new run's graph. We read
				// through a ref so the comparison after `await` sees the
				// latest snapshot identity (not the stale one captured by this
				// effect's closure).
				const snapshotAtStart = workflowSnapshot;

				setIsLayouting(true);
				try {
					// Use current graphNodes/graphEdges captured in this effect
					// Filter out scope container nodes - they don't participate in layout
					const filtered = graphNodes.filter(n => n.type !== 'scopeContainer');
					// In monitoring mode, strip ReactFlow's measured dimensions before
					// computing layout. DOM-measured widths vary per run because of
					// run-specific overlays (timing badges, status indicators, label
					// wrapping), and feeding them to Sugiyama produces different
					// positions for the same workflow across runs. Falling back to
					// DEFAULT_NODE_WIDTH/HEIGHT yields deterministic positions per
					// definition.
					const layoutNodes = isMonitoringMode
						? filtered.map((n) => {
							const { measured: _m, width: _w, height: _h, ...rest } = n;
							return rest;
						})
						: filtered;
					const layoutInput = convertToLayoutInput(layoutNodes, graphEdges);
					const direction = orientationToDirection(layoutOrientation);
					const positions = await calculateLayout(layoutInput, direction);

					if (positions.size > 0) {
						if (isMonitoringMode) {
							// Discard stale results from a previous snapshot. Compare
							// against the ref so we detect identity changes that
							// happened *during* the await.
							if (snapshotAtStart !== latestSnapshotRef.current) return;
							// Monitoring mode: keep positions local; never mutate the
							// editable store workflow (it is a different — possibly
							// newer — definition than the run's snapshot).
							setMonitoringPositions(positions);
						} else {
							// Skip dirty marking for initial layout - this is not a user change
							applyLayoutPositions(positions, true);
						}
						// Commit the fingerprint only after a real layout pass so a
						// transient "positions look present" render (e.g. stale
						// monitoringPositions for one render after a snapshot switch)
						// can't poison the guard and skip the actual layout.
						lastLayoutedWorkflowRef.current = workflowFingerprint;
					}
				} catch (error) {
					console.error("Initial auto-layout failed:", error);
				} finally {
					setIsLayouting(false);
					// Reveal canvas first, then fit viewport on next frame.
					// setTimeout ensures React has committed the new positions
					// before fitView measures node bounds — critical for large graphs.
					setIsInitialLayoutPending(false);
					fitViewTimerRef.current = window.setTimeout(() => {
						fitView({ padding: { top: 0.1, left: 0.1, bottom: 0.1, right: 0.35 }, duration: 300 });
					}, 50);
				}
			};

			// Run layout immediately
			applyInitialLayout();

			return () => {
				if (fitViewTimerRef.current != null) {
					clearTimeout(fitViewTimerRef.current);
					fitViewTimerRef.current = null;
				}
			};
		}
	}, [
		workflow,
		graphNodes,
		graphEdges,
		layoutOrientation,
		setIsLayouting,
		applyLayoutPositions,
		fitView,
		isMonitoringMode,
		workflowSnapshot,
		currentWorkflowIdentity,
		currentMode,
	]);

	// Handle panel close
	const handlePanelClose = useCallback(() => {
		setPanelState({
			isOpen: false,
			mode: "action",
			insertionPoint: null,
		});
		setClickPosition(null);
		setContextMenuPosition(null);
	}, []);

	// Unified close for the single PickerPanelHost mount. Dismisses whichever
	// view is currently active (operations or any auxiliary picker).
	const handlePickerHostClose = useCallback(() => {
		setAuxPickerView(null);
		handlePanelClose();
	}, [handlePanelClose]);

	// Single source of truth for which picker view the host should render.
	// Operations take precedence; otherwise show the auxiliary view, if any.
	const pickerView = useMemo<PickerView | null>(() => {
		if (panelState.isOpen) {
			return {
				kind: 'operations',
				mode: panelState.mode,
				insertionPoint: panelState.insertionPoint,
				...(panelState.targetAgentName !== undefined
					? { targetAgentName: panelState.targetAgentName }
					: {}),
				...(panelState.mode === 'tool' ? { title: 'Add Tool' } : {}),
			};
		}
		if (!auxPickerView) return null;
		if (auxPickerView.kind === 'subworkflow') {
			return {
				kind: 'subworkflow',
				targetAgentName: auxPickerView.targetAgentName,
				workflowName: workflowName ?? '',
			};
		}
		if (auxPickerView.kind === 'mcp') {
			// `workflowName` is needed by `useMcpToolsForConnection` so the
			// runtime listMcpTools call can be scoped to the right workflow.
			return {
				kind: 'mcp',
				targetAgentName: auxPickerView.targetAgentName,
				workflowName: workflowName ?? '',
			};
		}
		return auxPickerView;
	}, [panelState, auxPickerView, workflowName]);

	// Handle validation error click - selects and opens the node editor
	// The callback receives full error info including nodeName and error.path for precise navigation
	const handleValidationErrorClick = useCallback(
		(info: ValidationErrorClickInfo) => {
			const { nodeName } = info;
			// TODO: Use info.error.path for more precise parameter navigation in the future
			if (!nodeName) return; // Global errors don't have a node to navigate to

			// Check if this is a trigger or action
			const triggers = workflow?.definition?.triggers ?? {};
			const actions = workflow?.definition?.actions ?? {};

			if (nodeName in triggers) {
				selectNodeByName(nodeName, 'trigger');
			} else if (nodeName in actions) {
				selectNodeByName(nodeName, 'action');
			}
		},
		[workflow?.definition?.triggers, workflow?.definition?.actions, selectNodeByName]
	);

	// Handle drop from toolbox drag-and-drop
	const handleToolboxDrop = useCallback(
		(operation: OperationDefinition, position: { x: number; y: number }) => {
			// Determine if this is a trigger or action based on category
			if (operation.category === "trigger") {
				const triggerName = generateUniqueActionName(
					operation.name.replace(/\s+/g, "_"),
				);
				const trigger = createTriggerFromOperation(operation, position, validApiConnections, connectionReferences);
				addTrigger(triggerName, trigger);
				openNodeEditor({ id: `trigger-${triggerName}`, name: triggerName, type: "trigger" });
			} else {
				// Actions and control flow operations
				const actionName = generateUniqueActionName(
					operation.name.replace(/\s+/g, "_"),
				);
				const action: Action = createActionFromOperation(operation, position, validApiConnections, connectionReferences);

				// Hit-test against scoped branch terminal phantoms so dropping near a
				// control-flow branch endpoint (If true/false, ForEach loop, Switch
				// case/default) inserts inside that branch instead of becoming a
				// sibling of the control-flow node. See `findScopedBranchInsertion`
				// for lane geometry details.
				const dropIsHorizontal = layoutOrientation === 'horizontal';
				const scopedHit = findScopedBranchInsertion(
					position,
					terminalHandlesRef.current,
					getNodes(),
					dropIsHorizontal,
				);

				// When the drop is being nested into a branch, snap the action's
				// metadata position to the natural lane landing spot so the
				// serialized position matches the lane (rather than the precise
				// cursor location, which may be slightly outside the visual lane).
				const actionToInsert: Action = scopedHit
					? {
						...action,
						metadata: { ...(action.metadata ?? {}), position: scopedHit.snappedPosition },
					}
					: action;

				addAction(actionName, actionToInsert, scopedHit?.insertionPoint);
				openNodeEditor({ id: `action-${actionName}`, name: actionName, type: "action" });
			}
		},
		[generateUniqueActionName, addTrigger, addAction, openNodeEditor, validApiConnections, connectionReferences, getNodes, layoutOrientation]
	);

	// Use toolbox drag-drop hook with validation
	const {
		isDraggingOver,
		isValidDrop,
		invalidDropReason,
		handleDragOver: toolboxDragOver,
		handleDragLeave: toolboxDragLeave,
		handleDrop: toolboxDrop,
	} = useToolboxDragDrop({
		onOperationDrop: handleToolboxDrop,
		enabled: !isReadOnlyCanvas,
		hasTrigger: hasTriggers,
		dropZone: 'canvas',
	});

	// Helper to fetch swagger info for managed connectors
	// Returns path template and notification trigger inputs if applicable
	interface SwaggerInfo {
		pathTemplate?: string;
		notificationInputs?: {
			fetch: { method: string; pathTemplate: { template: string }; queries?: Record<string, unknown> };
			subscribe: { method: string; pathTemplate: { template: string }; body: Record<string, unknown>; queries?: Record<string, unknown> };
		};
	}

	const getSwaggerInfo = useCallback(async (operation: OperationDefinition): Promise<SwaggerInfo | undefined> => {
		// Only fetch for managed API connectors
		if (!isManagedApiConnector(operation.connectorId ?? '')) {
			return undefined;
		}

		// Extract operationId from the operation
		const operationId = extractOperationId(operation);
		if (!operationId) return undefined;

		try {
			const accessToken = await getOttoToken();
			if (!accessToken) return undefined;

			const swagger = await fetchConnectorSwagger(
				{
					projectName: selectedApp?.projectName ?? '',
					accessToken,
				},
				operation.connectorId ?? '',
			);

			if (!swagger) return undefined;

			const parser = new SwaggerParser(swagger);
			const swaggerOperation = parser.getOperationByOperationId(operationId);

			// For notification triggers, also get the notification inputs
			let notificationInputs: SwaggerInfo['notificationInputs'];
			if (operation.type === 'ApiConnectionNotification') {
				notificationInputs = parser.buildNotificationTriggerInputs(operationId) ?? undefined;
			}

			const result: SwaggerInfo = {};
			if (swaggerOperation?.path) {
				result.pathTemplate = swaggerOperation.path;
			}
			if (notificationInputs) {
				result.notificationInputs = notificationInputs;
			}
			return result;
		} catch (error) {
			console.warn('[WorkflowDesigner] Failed to fetch swagger info:', error);
			return undefined;
		}
	}, [getOttoToken, selectedApp?.projectName]);

	// Handle operation selection from panel
	const handleOperationSelect = useCallback(
		async (operation: OperationDefinition) => {
			// Block nested ForEach: prevent adding ForEach inside any ForEach scope
			if (panelState.insertionPoint?.scope && operation.type === 'Foreach') {
				const scope = panelState.insertionPoint.scope;
				let isInsideForEach = scope.branchType === 'loop';
				if (!isInsideForEach && storeWorkflow?.definition?.actions) {
					const parentContext = findScopeContext(scope.parentActionName, storeWorkflow.definition.actions);
					isInsideForEach = parentContext !== undefined;
				}
				if (isInsideForEach) {
					useNotificationStore.getState().addNotification({
						type: 'warning',
						title: FOREACH_NESTED_TITLE,
						message: FOREACH_NESTED_MESSAGE,
					});
					return;
				}
			}

			const isHorizontalLayout = layoutOrientation === "horizontal";

			// Fetch swagger info for managed connectors (async, non-blocking for UX)
			// This enables per-parameter path editing and proper notification trigger structure
			const swaggerInfo = await getSwaggerInfo(operation);
			const enhancedOperation: OperationDefinition = swaggerInfo?.pathTemplate
				? { ...operation, swaggerPathTemplate: swaggerInfo.pathTemplate }
				: operation;

			if (panelState.mode === "trigger") {
				// Add trigger with position from click (includes placeholder position) or default
				const triggerName = generateUniqueActionName(
					enhancedOperation.name.replace(/\s+/g, "_"),
				);
				const trigger = createTriggerFromOperation(
					enhancedOperation,
					clickPosition ?? DEFAULT_TRIGGER_POSITION,
					validApiConnections,
					connectionReferences,
					swaggerInfo?.notificationInputs,
				);
				addTrigger(triggerName, trigger);
				// Select the newly added trigger and open editor
				openNodeEditor({ id: `trigger-${triggerName}`, name: triggerName, type: "trigger" });
			} else if (panelState.mode === "tool" && panelState.targetAgentName) {
				// Adding a tool to an agent
				const agentName = panelState.targetAgentName;
				const toolName = generateUniqueToolName(
					agentName,
					enhancedOperation.name.replace(/\s+/g, "_"),
				);

				// Create the action that will be wrapped in the tool
				// Use buildConnectorInputs to set up host.apiId for managed connectors
				// Also pass connectionReferences to default to existing connection
				const inputs = buildConnectorInputs(enhancedOperation, validApiConnections, connectionReferences);
				// Tool metadata only carries serialization-relevant fields. Connector display
				// info (icon / brand / name) is resolved at render time from the connector
				// manifest by useWorkflowConnectors so it stays out of the persisted workflow.
				const toolMetadata: Record<string, unknown> = {
					...(enhancedOperation.swaggerPathTemplate && { swaggerPathTemplate: enhancedOperation.swaggerPathTemplate }),
				};
				const innerAction: Action = {
					type: enhancedOperation.type,
					...(enhancedOperation.kind ? { kind: enhancedOperation.kind } : {}),
					inputs,
					// Note: operationId is NOT added as a top-level action property.
					// Azure's API rejects unknown properties on FlowTemplateAction.
					// It's already stored in inputs.host.operationId by buildConnectorInputs.
					metadata: Object.keys(toolMetadata).length > 0 ? toolMetadata : undefined,
				};

				// Create the tool wrapper
				const tool: AgentTool = {
					description: `${enhancedOperation.name} tool`,
					actions: {
						[`${toolName}_Action`]: innerAction,
					},
				};

				// Add the tool to the agent
				addToolToAgent(agentName, toolName, tool);
				// Select the newly added tool
				selectTool(agentName, toolName);
			} else if (panelState.insertionPoint) {
				// Adding action with insertion point (between nodes or at end)
				// Find source and target node positions
				const sourceNode = graphNodes.find(
					(n) => n.id === panelState.insertionPoint?.sourceNodeId,
				);
				const targetNode = panelState.insertionPoint.targetNodeId
					? graphNodes.find(
							(n) => n.id === panelState.insertionPoint?.targetNodeId,
						)
					: undefined;

				// Calculate the number of existing parallel siblings when adding
				// a parallel branch (no targetNodeId). This allows us to offset
				// the new node's position to avoid overlap.
				const isTriggerSource = panelState.insertionPoint.sourceNodeId.startsWith('trigger-');
				const sourceActionName = isTriggerSource
					? panelState.insertionPoint.sourceNodeId.slice('trigger-'.length)
					: panelState.insertionPoint.sourceNodeId.slice('action-'.length);
				const isParallelBranch = !panelState.insertionPoint.targetNodeId && !panelState.insertionPoint.scope;
				const existingParallelSiblings = isParallelBranch
					? countParallelSiblings(workflowActions ?? {}, sourceActionName, isTriggerSource)
					: 0;

				// Calculate position for the new action
				const sourcePosition = sourceNode?.position ?? DEFAULT_TRIGGER_POSITION;
				const targetPosition = targetNode?.position;
				const newPosition = calculateInsertionPosition(
					sourcePosition,
					targetPosition,
					isHorizontalLayout,
					panelState.insertionPoint.scope,
					sourceNode?.measured,
					sourceNode?.id.startsWith('trigger-') ? DEFAULT_NODE_WIDTH : 0,
					existingParallelSiblings,
				);

				// Add action with calculated position in metadata
				const actionName = generateUniqueActionName(
					enhancedOperation.name.replace(/\s+/g, "_"),
				);
				const action: Action = createActionFromOperation(enhancedOperation, newPosition, validApiConnections, connectionReferences);
				addAction(actionName, action, panelState.insertionPoint);
				// Build scope context from insertion point if adding inside a scope
				const scopeContext = panelState.insertionPoint.scope
					? buildScopeContext(
							`action-${panelState.insertionPoint.scope.parentActionName}`,
							panelState.insertionPoint.scope.branchType,
						)
					: undefined;
				// Build the correct scoped node ID for selection
				const scopedActionId = panelState.insertionPoint.scope
					? `action-${panelState.insertionPoint.scope.parentActionName}/${actionName}`
					: `action-${actionName}`;
				// Select the newly added action and open editor
				openNodeEditor({ id: scopedActionId, name: actionName, type: "action", ...(scopeContext && { scopeContext }) });
			} else {
				// Adding floating action at clicked position or calculated position to avoid overlap
				const actionName = generateUniqueActionName(
					enhancedOperation.name.replace(/\s+/g, "_"),
				);
				const floatingPosition = clickPosition ?? calculateFloatingNodePosition(
					graphNodes.map(n => n.position),
					layoutOrientation === 'horizontal',
				);
				const action: Action = createActionFromOperation(
					enhancedOperation,
					floatingPosition,
					validApiConnections,
					connectionReferences,
				);
				addAction(actionName, action);
				// Select the newly added action and open editor
				openNodeEditor({ id: `action-${actionName}`, name: actionName, type: "action" });
			}

			// Close panel after adding
			handlePanelClose();
		},
		[panelState.insertionPoint, panelState.mode, panelState.targetAgentName, validApiConnections, layoutOrientation, getSwaggerInfo, handlePanelClose, storeWorkflow?.definition?.actions, generateUniqueActionName, clickPosition, connectionReferences, addTrigger, openNodeEditor, generateUniqueToolName, addToolToAgent, selectTool, graphNodes, workflowActions, addAction],
	);

	// MiniMap: memoized node color function (must be before early returns per rules-of-hooks)
	const minimapNodeColor = useCallback((node: Node) => {
		switch (node.type) {
			case 'trigger':
			case 'placeholderTrigger':
				return ottoTokens.nodeAccentTrigger;
			case 'agent':
				return ottoTokens.nodeAccentAgent;
			case 'if':
			case 'switch':
			case 'foreach':
				return ottoTokens.nodeAccentControlFlow;
			case 'tool':
				return ottoTokens.nodeAccentTool;
			case 'model':
				return ottoTokens.nodeAccentModel;
			case 'note':
			case 'scopeContainer':
				return ottoTokens.canvasMinimapNote;
			case 'phantom':
			case 'merge':
				return 'transparent';
			default:
				return ottoTokens.nodeAccentAction;
		}
	}, []);

	// Hide minimap for small workflows where it adds no navigational value
	const visibleNodeCount = useMemo(
		() => nodes.filter(n => !n.hidden && n.type !== 'phantom' && n.type !== 'merge').length,
		[nodes]
	);

	// Show loading state in monitoring mode
	if (isMonitoringMode && isLoading) {
		return (
			<div className={styles.loadingState}>
				<Spinner size="medium" />
				<Text className={styles.loadingText}>Loading execution...</Text>
			</div>
		);
	}

	// Show empty state if no workflow loaded or workflow exists but has no triggers/actions
	// Skip empty state if chat sidebar is open (user is using AI assistant, show canvas with placeholder trigger)
	// In monitoring mode, differentiate between missing workflow and no run selected
	// Shared template gallery dialog — rendered in both empty-canvas and normal designer paths
	const templateGalleryElement = (
		isWorkflowTemplatesEnabled ? (
			<TemplateGalleryDialog
				open={isTemplateGalleryOpen}
				onDismiss={handleTemplateGalleryDismiss}
				initialPreviewTemplateId={initialPreviewTemplateId}
			/>
		) : null
	);

	// IMPORTANT: Check both hasTriggers and hasActions to avoid race condition where
	// workflow has loaded but graphNodes hasn't been computed yet
	const skipEmptyState = isChatSidebarOpen;
	if (!skipEmptyState && (!workflow || (isCanvasEmpty && graphNodes.length === 0))) {
		if (isMonitoringMode) {
			// Differentiate between missing workflow definition and no run selected
			const message = !workflow
				? "No workflow definition available"
				: "Select a run or execution to view the workflow";
			return (
				<div className={styles.loadingState}>
					<Text className={styles.loadingText}>{message}</Text>
				</div>
			);
		}

		// Show skeleton state while assistant is generating workflow
		if (isStreaming) {
			return (
				<div className={styles.wrapper}>
					<CanvasSkeletonState
						statusMessage={currentStatus ?? 'Generating your workflow...'}
						userPrompt={firstUserMessage}
					/>
				</div>
			);
		}

		// Show empty canvas state with toolbar visible
		// User can either use the chat input to generate workflow, or drag from toolbar
		const showEmptyCommandBar = !isReadOnlyCanvas && onToggleCode && onToggleChat;

		// Handler for dropping operations from the empty canvas drop zone
		const handleEmptyCanvasDrop = (operation: OperationDefinition) => {
			// Use center of canvas as default position
			const position = { x: 250, y: 150 };

			// Determine if this is a trigger or action based on category
			if (operation.category === "trigger") {
				const triggerName = generateUniqueActionName(
					operation.name.replace(/\s+/g, "_"),
				);
				const trigger = createTriggerFromOperation(operation, position, validApiConnections, connectionReferences);
				addTrigger(triggerName, trigger);
				openNodeEditor({ id: `trigger-${triggerName}`, name: triggerName, type: "trigger" });
			} else {
				// Actions and control flow operations
				const actionName = generateUniqueActionName(
					operation.name.replace(/\s+/g, "_"),
				);
				const action: Action = createActionFromOperation(operation, position, validApiConnections, connectionReferences);
				addAction(actionName, action);
				openNodeEditor({ id: `action-${actionName}`, name: actionName, type: "action" });
			}
		};

		return (
			<div ref={emptyCanvasWrapperRef} className={styles.wrapper}>
				<EmptyCanvasState
					{...(!isReadOnlyCanvas && { onAddTrigger: handleAddTriggerFromMenu })}
					{...(!isReadOnlyCanvas && { onOperationDrop: handleEmptyCanvasDrop })}
					{...(isWorkflowTemplatesEnabled && !isReadOnlyCanvas && { onBrowseTemplates: handleBrowseTemplates })}
				/>
				{/* Show toolbar on empty canvas so users can drag to build manually */}
				{showEmptyCommandBar && (
					<DockToolbar
						containerRef={emptyCanvasWrapperRef}
						isCodeVisible={false}
						onToggleCode={onToggleCode}
						isChatOpen={isChatSidebarOpen}
						onToggleChat={onToggleChat}
						isDirty={false}
						isSaving={false}
						isConversational={isConversational}
						disableCommands={isStreaming}
						draftModeEnabled={draftModeEnabled}
						workflowName={workflowName}
						onOperationSelect={handleOperationSelect}
						onBrowseActions={handleBrowseActions}
						onBrowseTriggers={handleBrowseTriggers}
						onBrowseMCP={handleBrowseMCP}
						onBrowseSubworkflow={handleBrowseSubworkflow}
						hasTrigger={false}
						isExternalPickerOpen={pickerView !== null}
						{...(isWorkflowTemplatesEnabled && { onBrowseTemplates: handleBrowseTemplates })}
					/>
				)}
				<PickerPanelHost
					view={pickerView}
					onOperationSelect={handleOperationSelect}
					onMcpServerSelect={handleMCPServerSelect}
					onSubworkflowSelect={handleSubworkflowSelect}
					onAgentTypeSelect={handleAgentTypeSelectFromEdge}
					onClose={handlePickerHostClose}
				/>
				{templateGalleryElement}
				<NodeEditor />
				<ToolTypeMenu
					open={toolTypeMenuState.open}
					anchorEl={toolTypeMenuState.anchorEl}
					onSelect={handleToolTypeSelect}
					onClose={handleToolTypeMenuClose}
				/>
			</div>
		);
	}

	const isHorizontal = layoutOrientation === "horizontal";

	// Determine if we should show the command bar (need at least toggle functions)
	const showCommandBar = !isReadOnlyCanvas && onToggleCode && onToggleChat;

	const showMinimap = !isCanvasEmpty && !isMonitoringMode && visibleNodeCount > 3;

	return (
		<div className={styles.wrapper}>
			{/* Context menus - only shown in design mode */}
			{!isReadOnlyCanvas && (
				<>
					<CanvasContextMenu
						isOpen={isContextMenuOpen}
						position={contextMenuPosition}
						onClose={handleCloseContextMenu}
						onAddTrigger={handleAddTriggerFromMenu}
						onAddAction={handleAddActionFromMenu}
						onAddNote={handleAddNoteFromMenu}
						onAutoArrange={handleAutoLayout}
						isAutoArranging={isLayouting}
						onBackToStart={handleBackToStart}
						showBackToStart={false}
						sticky={false}
					/>
					<NodeContextMenu
						isOpen={isNodeContextMenuOpen}
						position={nodeContextMenuPosition}
						nodeId={contextMenuNodeId}
						nodeType={contextMenuNodeType}
						onClose={handleCloseNodeContextMenu}
						onDelete={handleDeleteNode}
						onEditWithAssistant={handleEditWithAssistant}
						onAddParallelBranch={handleAddParallelBranch}
					/>
					<NoteContextMenu
						isOpen={isNoteContextMenuOpen}
						position={noteContextMenuPosition}
						noteId={contextMenuNoteId}
						currentColor={contextMenuNoteColor}
						onClose={handleCloseNoteContextMenu}
						onDelete={handleDeleteNoteFromContextMenu}
						onDuplicate={handleDuplicateNote}
						onChangeColor={handleChangeNoteColor}
					/>
					<EdgeContextMenu
						isOpen={isEdgeContextMenuOpen}
						position={edgeContextMenuPosition}
						edgeId={contextMenuEdgeId}
						sourceNodeId={contextMenuEdgeSourceId}
						isScopeEdge={contextMenuEdgeIsScopeEdge}
						sourceHandle={contextMenuEdgeSourceHandle}
						onClose={handleCloseEdgeContextMenu}
						onDelete={handleDeleteEdge}
						onAddParallelBranch={handleAddParallelBranch}
					/>
				</>
			)}
			<div
				ref={canvasContainerRef}
				className={mergeClasses(
				styles.container,
				isDraggingOver && isValidDrop && styles.containerDragOver,
				!isValidDrop && styles.containerDragInvalid
			)}>
				{/* Drop rejection feedback */}
				{!isValidDrop && invalidDropReason && (
					<div className={styles.dropFeedback}>
						{invalidDropReason}
					</div>
				)}
				{/* Connections loading indicator */}
				{isLoadingConnections && (
					<div className={styles.connectionsLoading}>
						<Spinner size="tiny" />
						<Text className={styles.connectionsLoadingText}>Loading...</Text>
					</div>
				)}
				<ReactFlow
					style={isInitialLayoutPending ? { opacity: 0 } : undefined}
					nodes={nodes}
					edges={edges}
					onNodeClick={handleNodeClick}
					onSelectionEnd={handleSelectionEnd}
					nodeTypes={nodeTypes}
					edgeTypes={edgeTypes}
					defaultEdgeOptions={{
						style: { strokeWidth: 2 },
					}}
					proOptions={{ hideAttribution: true }}
					fitView
					fitViewOptions={{
						padding: { top: 0.1, left: 0.1, bottom: 0.1, right: 0.35 },
						minZoom: 0.5,
						maxZoom: 1,
					}}
					panOnScroll
					zoomOnScroll={false}
					zoomOnPinch
					nodesDraggable={!isReadOnlyCanvas && !isCanvasLocked}
					nodesConnectable={!isReadOnlyCanvas && !isCanvasLocked}
					elementsSelectable={!isCanvasLocked}
					selectionOnDrag
					selectionKeyCode="Shift"
					multiSelectionKeyCode="Shift"
					deleteKeyCode={null}
					snapToGrid
					snapGrid={[1, 1]}
					isValidConnection={isValidConnection}
					{...(!isReadOnlyCanvas && {
						onNodesChange: handleNodesChange,
						onEdgesChange: handleEdgesChange,
						onConnect: handleConnect,
						onNodeContextMenu: handleNodeContextMenu,
						onEdgeContextMenu: handleEdgeContextMenu,
						onPaneContextMenu: handlePaneContextMenu,
						onPaneClick: handlePaneClick,
						onMove: handleMove,
						onDragOver: toolboxDragOver,
						onDragLeave: toolboxDragLeave,
						onDrop: toolboxDrop,
					})}
					{...(isReadOnlyCanvas && {
						onNodesChange: handleMonitoringNodesChange,
					})}
				>
					{/* Hide controls on empty canvas - nothing to zoom/arrange */}
					{/* Position bottom-right */}
					{!isCanvasEmpty && (
						<Controls
							position="bottom-right"
							showZoom={false}
							showFitView={false}
							showInteractive={false}
						>
							<CanvasControls
								isHorizontal={isHorizontal}
								isLayouting={isLayouting}
								onAutoArrange={handleAutoLayout}
								onToggleOrientation={handleOrientationToggle}
								showAutoArrange={!isReadOnlyCanvas}
								showInteractive={!isReadOnlyCanvas}
								isLocked={isCanvasLocked}
								onToggleLock={handleToggleCanvasLock}
							/>
						</Controls>
					)}
					<Background variant={BackgroundVariant.Dots} />
					{/* MiniMap — hidden for small workflows and monitoring */}
					{showMinimap && (
						<MiniMap
							position="bottom-left"
							pannable
							zoomable
							nodeColor={minimapNodeColor}
							nodeComponent={MiniMapNode}
							nodeBorderRadius={0}
							aria-label="Workflow minimap"
						/>
					)}
					{/* Canvas search - design mode only */}
					{!isMonitoringMode && (
						<CanvasSearch
							nodes={nodes}
							isOpen={isSearchOpen}
							onClose={() => {
								setIsSearchOpen(false);
								telemetryService.trackEvent('canvas.search.closed');
							}}
						/>
					)}
				</ReactFlow>
				{/* Template mode toolbar hidden — controls moved to header WorkflowViewTabs */}
				{/* Dock toolbar - only in design mode with required props */}
				{showCommandBar && !isLoadingConnections && (
					<DockToolbar
						containerRef={canvasContainerRef}
						isCodeVisible={isCodeVisible}
						onToggleCode={onToggleCode}
						isChatOpen={isChatSidebarOpen}
						onToggleChat={onToggleChat}
						isDirty={isDirty}
						isSaving={isSaving}
						isConversational={isConversational}
						isValidating={isValidating}
						disableCommands={isStreaming}
						draftModeEnabled={draftModeEnabled}
						workflowName={workflowName}
						onOperationSelect={handleOperationSelect}
						onBrowseActions={handleBrowseActions}
						onBrowseTriggers={handleBrowseTriggers}
						onBrowseMCP={handleBrowseMCP}
						onBrowseSubworkflow={handleBrowseSubworkflow}
						hasTrigger={hasTriggers}
						onAddNote={handleAddNoteFromDock}
						isExternalPickerOpen={pickerView !== null}
						{...(isWorkflowTemplatesEnabled && { onBrowseTemplates: handleBrowseTemplates })}
						{...(onSave && { onSave })}
						{...(onStartChat && { onStartChat })}
						{...(onRun && { onRun })}
						{...(onValidate && { onValidate })}
						{...(validationErrorCount !== undefined && { validationErrorCount })}
						onValidationErrorClick={handleValidationErrorClick}
					/>
				)}
			</div>
			{/* Picker panel host (operation / MCP / sub-workflow / agent-type) and node editor - only in design mode */}
			{!isMonitoringMode && (
				<>
					<PickerPanelHost
						view={pickerView}
						onOperationSelect={handleOperationSelect}
						onMcpServerSelect={handleMCPServerSelect}
						onSubworkflowSelect={handleSubworkflowSelect}
						onAgentTypeSelect={handleAgentTypeSelectFromEdge}
						onClose={handlePickerHostClose}
					/>
					{templateGalleryElement}
					<NodeEditor />
				</>
			)}
			{/* Tool type menu - shown when clicking Add Tool on agent */}
			<ToolTypeMenu
				open={toolTypeMenuState.open}
				anchorEl={toolTypeMenuState.anchorEl}
				onSelect={handleToolTypeSelect}
				onClose={handleToolTypeMenuClose}
			/>
		</div>
	);
};

export const WorkflowDesigner = (props: WorkflowDesignerMonitoringProps = {}) => {
	const { draftModeEnabled = false, workflowName = '' } = props;
	
	const content = (
		<ReactFlowProvider>
			<WorkflowDesignerContent {...props} />
		</ReactFlowProvider>
	);
	
	// Wrap with DraftModeProvider when draft mode is enabled
	if (draftModeEnabled && workflowName) {
		return (
			<DraftModeProvider workflowName={workflowName}>
				{content}
			</DraftModeProvider>
		);
	}
	
	return content;
};
